import React, { useState, useEffect, useContext, useCallback, useMemo, useRef } from "react";
import NavigationBar from "../components/Navigation";
import axios from "axios";
import { Container, Button } from "reactstrap";
import Actor from "../components/Actor";
import { useParams, useNavigate } from "react-router-dom";
import Footer from "../components/Footer";
import Genre from "../components/Genre";
import YouTube from "react-youtube";
import { Link } from "react-router-dom";
import { Doughnut, Pie } from "react-chartjs-2";
import { Chart, ArcElement, LineElement, BarElement, PointElement, BarController, LineController, PieController, CategoryScale, LinearScale, RadialLinearScale, TimeScale } from 'chart.js';
import ActeurContext from '../contexts/ActeurContext';
import { UserContext } from '../UserContext';
import Flag from 'react-world-flags';
import { db, auth } from "../config/firebasedeconfig";
import {
    doc,
    getDoc,
    updateDoc,
    runTransaction,
    serverTimestamp,
    collection,
    addDoc,
    query,
    where,
    getDocs,
    limit,
    increment,
    deleteField,
    collectionGroup,
    writeBatch,
} from "firebase/firestore";
import { Helmet } from "react-helmet-async"; // Ajout de Helmet pour le SEO
import { useLanguage } from '../contexts/LanguageContext';
import ActivityGainToast from "../components/ActivityGainToast";
import cld from '../config/cloudinaryConfig';
import { format, quality } from '@cloudinary/url-gen/actions/delivery';
import { findQuebecRating } from "../utils/quebecRatingUtils";
import {
    getMovieVideoFallbacks
} from "../utils/movieVideoFallbacks";
import {
    findQuebecProduction
} from "../utils/quebecProductionUtils";
import {
    fetchOmdbMovieData
} from "../utils/omdbUtils";
import OmdbInsightsPopover from "../components/OmdbInsightsPopover";
import {
    buildMaturityRating,
    formatCertificationLabel,
    getMaturityBadgePresentation,
    getQuebecAdvisoryLabels,
    normalizeCertificationCode,
    resolveRatingSystem,
} from "../utils/movieMaturityUtils";
import { fetchKinoCheckTrailers } from "../utils/kinocheckUtils";
import { deleteForumReply } from "../services/forumActivityService";


Chart.register(
    ArcElement,
    LineElement,
    BarElement,
    PointElement,
    BarController,
    LineController,
    PieController,
    CategoryScale,
    LinearScale,
    RadialLinearScale,
    TimeScale
);

const TMDB_API_KEY = process.env.REACT_APP_TMDB_API_KEY;

const darkwaterImageUrl = cld
    .image('darkwater_jkk0bl')
    .delivery(quality('auto'))
    .delivery(format('auto'))
    .toURL();

const logoPinkTownBgUrl = cld
    .image('logoFooterChatGPT26_oxnohb')
    .delivery(quality('auto'))
    .delivery(format('auto'))
    .toURL();

const TMDB_LOCALES = {
    fr: "fr-FR",
    en: "en-US",
    es: "es-ES",
    de: "de-DE",
};

const getTmdbLocale = (language) => {
    return TMDB_LOCALES[language] || language || "en-US";
};

const normalizeVideoTitle = (title = "") => {
    return String(title)
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();
};

const isSecondaryPromotionalVideo = (video) => {
    const normalizedTitle = normalizeVideoTitle(video?.name);

    const secondaryPatterns = [
        /\bteaser\b/,
        /\b(?:tv\s*)?spot\b/,
        /\bcountdown\b/,
        /\bin cinemas now\b/,
        /\bnow playing\b/,
        /\bj\s*-\s*\d+\b/,

        // Promotions particulières ou centrées sur un aspect du film.
        /\bspecial\s+(?:trailer|pv)\b/,
        /\bopening\s+(?:theme|song)\b/,
        /\bintroducing\b/,
        /\bcharacter\s+(?:trailer|promo|video)\b/,

        // Variantes dans certaines langues.
        /\bbande[-\s]?annonce\s+speciale?\b/,
        /\btrailer\s+especial\b/,
        /\bspezialtrailer\b/,

        // Variantes japonaises fréquentes.
        /スペシャル\s*(?:pv|trailer)/,
        /オープニング(?:テーマ|主題歌)?/,
        /キャラクター\s*(?:pv|trailer)?/,
    ];

    return secondaryPatterns.some((pattern) =>
        pattern.test(normalizedTitle)
    );
};

const getTrailerScore = (video, preferredLanguage) => {
    const normalizedTitle = normalizeVideoTitle(video.name);
    let score = 0;

    // Priorité très forte aux vidéos reconnues comme officielles par TMDB.
    if (video.official === true) {
        score += 1000;
    }

    // Priorité à la langue actuellement sélectionnée.
    if (video.iso_639_1 === preferredLanguage) {
        score += 150;
    }

    // Bonus lorsque le titre lui-même mentionne son caractère officiel.
    if (
        /\bofficial\b/.test(normalizedTitle) ||
        /\bofficiel(?:le)?\b/.test(normalizedTitle) ||
        /\boficial\b/.test(normalizedTitle) ||
        /\boffiziell(?:e|er|es|en)?\b/.test(normalizedTitle)
    ) {
        score += 100;
    }

    // En français, une VF passe avant une VOST.
    if (preferredLanguage === "fr") {
        if (/\bvf\b/.test(normalizedTitle)) {
            score += 60;
        }

        if (/\bvost(?:fr)?\b/.test(normalizedTitle)) {
            score += 20;
        }
    }

    // Les trailers finaux et numérotés restent acceptables,
    // mais passent après une bande-annonce principale équivalente.
    if (/\bfinal(?:e|er|es)?\b/.test(normalizedTitle)) {
        score -= 25;
    }

    if (
        /(?:#|n[°o]?\s*)\d+\b|\btrailer\s*\d+\b/.test(normalizedTitle)
    ) {
        score -= 15;
    }

    // Petit avantage à la meilleure résolution renseignée par TMDB.
    if (Number.isFinite(Number(video.size))) {
        score += Math.min(Number(video.size), 2160) / 100;
    }

    return score;
};

const isUsableYouTubeVideo = (video) => {
    return (
        video?.site?.toLowerCase() === "youtube" &&
        Boolean(video.key)
    );
};

const getNormalizedVideoType = (video) => {
    return String(video?.type || "")
        .trim()
        .toLowerCase();
};

const sortTrailerCandidates = (
    videos = [],
    preferredLanguage
) => {
    return [...videos].sort((firstVideo, secondVideo) => {
        const scoreDifference =
            getTrailerScore(secondVideo, preferredLanguage) -
            getTrailerScore(firstVideo, preferredLanguage);

        if (scoreDifference !== 0) {
            return scoreDifference;
        }

        // En cas d’égalité, prendre la publication la plus récente.
        return (
            new Date(secondVideo.published_at || 0).getTime() -
            new Date(firstVideo.published_at || 0).getTime()
        );
    });
};

const rankVideoCandidates = (
    videos = [],
    preferredLanguage
) => {
    const usableVideos = videos.filter(
        isUsableYouTubeVideo
    );

    const usableTrailers = usableVideos.filter(
        (video) =>
            getNormalizedVideoType(video) === "trailer"
    );

    const mainTrailerCandidates = usableTrailers.filter(
        (video) => !isSecondaryPromotionalVideo(video)
    );

    const backupTrailerCandidates = usableTrailers.filter(
        isSecondaryPromotionalVideo
    );

    const behindScenesCandidates = usableVideos.filter(
        (video) => {
            const videoType = getNormalizedVideoType(video);

            return (
                videoType === "behind the scenes" ||
                videoType === "featurette"
            );
        }
    );

    const bonusCandidates = usableVideos.filter(
        (video) => {
            const videoType = getNormalizedVideoType(video);

            return (
                videoType === "clip" ||
                videoType === "teaser" ||
                videoType === "bloopers"
            );
        }
    );

    return {
        main: sortTrailerCandidates(
            mainTrailerCandidates,
            preferredLanguage
        ),

        backup: sortTrailerCandidates(
            backupTrailerCandidates,
            preferredLanguage
        ),

        behindScenes: sortTrailerCandidates(
            behindScenesCandidates,
            preferredLanguage
        ),

        bonus: sortTrailerCandidates(
            bonusCandidates,
            preferredLanguage
        ),
    };
};

const isValidRatingMode = (mode) => {
    return mode === "tmdb" || mode === "moviefrenzy";
};

const escapeHtml = (value = "") => {
    return String(value)
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;")
        .replaceAll('"', "&quot;")
        .replaceAll("'", "&#039;");
};

const formatMovieReviewContent = ({ movieTitle, rating, review }) => {
    const safeTitle = escapeHtml(movieTitle);
    const safeReview = escapeHtml(review).replace(/\n/g, "<br />");

    return `
        <p><strong>Note MovieFrenzy : ${rating}/10</strong></p>
        <p><strong>Film :</strong> ${safeTitle}</p>
        <p>${safeReview}</p>
    `;
};

const extractReviewTextFromForumContent = (htmlContent = "") => {
    const normalizedHtml = String(htmlContent).replace(/<br\s*\/?>/gi, "\n");

    const parser = new DOMParser();
    const parsedDocument = parser.parseFromString(normalizedHtml, "text/html");

    const paragraphs = Array.from(parsedDocument.querySelectorAll("p"));

    if (paragraphs.length >= 3) {
        return paragraphs
            .slice(2)
            .map((paragraph) => paragraph.textContent.trim())
            .filter(Boolean)
            .join("\n\n");
    }

    return parsedDocument.body.textContent?.trim() || "";
};

const createMovieReviewExcerpt = (reviewText = "", maxLength = 180) => {
    const normalizedText = String(reviewText || "")
        .replace(/\s+/g, " ")
        .trim();

    if (normalizedText.length <= maxLength) {
        return normalizedText;
    }

    return `${normalizedText.slice(0, maxLength - 1).trim()}…`;
};

/*
 * Le registre québécois emploie certains codes
 * nationaux différents des codes ISO utilisés
 * par TMDB et par le composant Flag.
 */
const REGISTRY_NATION_TO_ISO2 = new Map([
    ["SUE", "SE"],
]);

const FicheFilm = (props) => {
    const { t, currentLanguage } = useLanguage();
    const countryNamesFormatter = useMemo(() => {
        try {
            return new Intl.DisplayNames(
                [currentLanguage],
                { type: "region" }
            );
        } catch (error) {
            return null;
        }
    }, [currentLanguage]);
    const { id } = useParams();
    const [movie, setMovie] = useState({});
    const [detail, setDetail] = useState({
        writer: "",
    });

    const [omdbData, setOmdbData] =
        useState(null);

    const [maturityRating, setMaturityRating] =
        useState(null);
    const [
        quebecProductionInfo,
        setQuebecProductionInfo
    ] = useState(null);
    const [genres, setGenres] = useState([]);
    const [actors, setActors] = useState([]);
    const [directors, setDirectors] = useState([]);
    const [credits, setCredits] = useState([]);
    const history = useNavigate();
    const [videoKey, setVideoKey] = useState("");

    const [activeVideoCategory, setActiveVideoCategory] =
        useState("trailer");

    const [
        videoCandidatesByCategory,
        setVideoCandidatesByCategory
    ] = useState({
        trailer: [],
        behindScenes: [],
        bonus: [],
    });

    const videoCandidates =
        videoCandidatesByCategory[activeVideoCategory] || [];
    const videoCategoryOrder = [
        "trailer",
        "behindScenes",
        "bonus",
    ];

    const videoCategoryLabels = {
        trailer: t("movie.videoCategories.trailer"),
        behindScenes: t("movie.videoCategories.behindScenes"),
        bonus: t("movie.videoCategories.bonus"),
    };

    const availableVideoCategories =
        videoCategoryOrder.filter(
            (category) =>
                videoCandidatesByCategory[category].length > 0
        );

    // Le sélecteur est inutile lorsqu’une bande-annonce
    // est la seule catégorie disponible.
    // Une catégorie unique différente de Trailer reste affichée
    // afin d’indiquer clairement le type de vidéo présenté.
    const shouldShowVideoCategorySelector =
        availableVideoCategories.length > 1 ||
        (
            availableVideoCategories.length === 1 &&
            availableVideoCategories[0] !== "trailer"
        );
    const rejectedVideoKeysRef = useRef(new Set());
    const videoLoadRef = useRef(null);
    const [isKinoLoading, setIsKinoLoading] = useState(false);
    const overviewSectionRef = useRef(null);
    const tmdbRatingsCount =
        Number.isInteger(detail.vote_count) && detail.vote_count > 0
            ? detail.vote_count
            : 0;

    const tmdbPercent =
        tmdbRatingsCount > 0 && Number.isFinite(detail.vote_average)
            ? (detail.vote_average * 10).toFixed(2)
            : null;
    const [movieFrenzyAverageRating, setMovieFrenzyAverageRating] = useState(null);
    const [movieFrenzyRatingsCount, setMovieFrenzyRatingsCount] = useState(0);
    const [userMovieRating, setUserMovieRating] = useState(null);
    const [selectedUserMovieRating, setSelectedUserMovieRating] = useState(8);
    const [userRatingMessage, setUserRatingMessage] = useState("");
    const [isSavingUserRating, setIsSavingUserRating] = useState(false);
    const [isMovieReviewModalOpen, setIsMovieReviewModalOpen] = useState(false);
    const [movieReviewDraft, setMovieReviewDraft] = useState("");
    const [isUserRatingLoading, setIsUserRatingLoading] = useState(false);
    const movieFrenzyPercent = movieFrenzyAverageRating === null
        ? null
        : (Number(movieFrenzyAverageRating) * 10).toFixed(2);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [showLimitMessage, setShowLimitMessage] = useState(false);
    const [showAlreadyInFavorites, setShowAlreadyInFavorites] = useState(false);
    const [animatedPercent, setAnimatedPercent] = useState("0.00");
    const [isVisible, setIsVisible] = useState(false);
    const {
        isLoggedIn,
        userId,
        username,
        userData,
    } = useContext(UserContext);
    const ratingSystem = useMemo(() => {
        return resolveRatingSystem({
            country: userData?.country,
            state: userData?.state,
            language: currentLanguage,
        });
    }, [
        userData?.country,
        userData?.state,
        currentLanguage,
    ]);

    const maturityBadge = useMemo(() => {
        return getMaturityBadgePresentation({
            maturityRating,
            requestedSystem: ratingSystem,
        });
    }, [
        maturityRating,
        ratingSystem,
    ]);

    const maturityLabel =
        maturityRating?.label ||
        t("movie.rating.maturity.unrated");

    const isOfficialQuebecRating =
        maturityRating?.source ===
        "quebec-official";

    const isQuebecProduction =
        quebecProductionInfo
            ?.isQuebecProduction === true;

    const displayedProductionCountries =
        useMemo(() => {
            const countries = [];
            const seenCodes = new Set();

            const addCountry = (country) => {
                const isoCode = String(
                    country?.iso_3166_1 ?? ""
                )
                    .trim()
                    .toUpperCase();

                if (
                    !isoCode ||
                    seenCodes.has(isoCode)
                ) {
                    return;
                }

                seenCodes.add(isoCode);

                countries.push({
                    ...country,
                    iso_3166_1: isoCode,
                    isQuebec: false,
                });
            };

            let quebecInserted = false;

            const addQuebec = () => {
                if (
                    !isQuebecProduction ||
                    quebecInserted
                ) {
                    return;
                }

                countries.push({
                    iso_3166_1: "QC",
                    name: "Québec",
                    isQuebec: true,
                });

                seenCodes.add("QC");
                quebecInserted = true;
            };

            for (
                const country of
                detail.production_countries ?? []
            ) {
                addCountry(country);

                /*
                 * Le Québec est placé immédiatement
                 * après le Canada lorsqu’il est présent.
                 */
                if (
                    String(
                        country?.iso_3166_1
                    ).toUpperCase() === "CA"
                ) {
                    addQuebec();
                }
            }

            /*
             * Permet d’afficher Québec même lorsque
             * TMDB omet complètement le Canada.
             */
            addQuebec();

            for (
                const nation of
                quebecProductionInfo?.nations ?? []
            ) {
                const registryCode =
                    String(nation)
                        .trim()
                        .toUpperCase();

                const isoCode =
                    REGISTRY_NATION_TO_ISO2.get(
                        registryCode
                    );

                if (isoCode) {
                    addCountry({
                        iso_3166_1: isoCode,
                    });
                }
            }

            return countries;
        }, [
            detail.production_countries,
            isQuebecProduction,
            quebecProductionInfo?.nations,
        ]);

    const quebecAdvisoryLabels =
        getQuebecAdvisoryLabels(
            maturityRating?.advisories,
            t
        );

    const [movieReviewForumPostId, setMovieReviewForumPostId] = useState(null);
    const [movieReviewRepliesCount, setMovieReviewRepliesCount] = useState(0);
    const [ratingMode, setRatingMode] = useState("tmdb");
    const [movieReviewMeta, setMovieReviewMeta] = useState(null);
    const [isDeletingMovieReview, setIsDeletingMovieReview] = useState(false);
    const [activityGainToast, setActivityGainToast] = useState(null);
    const [chartData, setChartData] = useState({
        datasets: [
            {
                data: [0, 100],
                backgroundColor: ["#FF33CC", "#333333"],
                borderWidth: 0,
            },
        ],
    });
    const [chartTextStyle, setChartTextStyle] = useState({});

    const isMovieFrenzyRatingMode = ratingMode === "moviefrenzy";

    const canShareMovieFrenzyReview =
        isLoggedIn &&
        userId &&
        isMovieFrenzyRatingMode &&
        !isUserRatingLoading &&
        userMovieRating !== null;

    const hasExistingMovieReview =
        movieReviewMeta?.hasReview === true &&
        Boolean(movieReviewMeta?.forumPostId) &&
        Boolean(movieReviewMeta?.forumReplyId);

    // const movieReviewActionLabel = hasExistingMovieReview
    //     ? t("movie.rating.editReview")
    //     : t("movie.rating.shareReview");

    const movieReviewSubmitLabel = hasExistingMovieReview
        ? t("movie.rating.updateReview")
        : t("movie.rating.publishReview");

    const movieReviewPlaceholder = hasExistingMovieReview
        ? t("movie.rating.editReviewPlaceholder")
        : t("movie.rating.reviewPlaceholder");

    const movieReviewModalTitle = hasExistingMovieReview
        ? t("movie.rating.editReviewModalTitle")
        : t("movie.rating.reviewModalTitle");

    const safeMovieReviewRepliesCount = Number(movieReviewRepliesCount) || 0;

    const movieReviewLinkLabel =
        safeMovieReviewRepliesCount > 0
            ? safeMovieReviewRepliesCount === 1
                ? t("movie.rating.readMovieFrenzyReviews_one").replace("{{count}}", safeMovieReviewRepliesCount)
                : t("movie.rating.readMovieFrenzyReviews_other").replace("{{count}}", safeMovieReviewRepliesCount)
            : t("movie.rating.readMovieFrenzyReviews");

    const activeRatingPercent = ratingMode === "tmdb"
        ? tmdbPercent
        : movieFrenzyPercent;

    const activeChartPercent = activeRatingPercent === null
        ? 0
        : Number(activeRatingPercent);

    const activeChartColors = useMemo(() => (
        isMovieFrenzyRatingMode
            ? ["#00FFFF", "#2B0013"]
            : ["#FF33CC", "#333333"]
    ), [isMovieFrenzyRatingMode]);

    const activeChartHoverColors = useMemo(() => (
        isMovieFrenzyRatingMode
            ? ["#7CFFFF", "#200211"]
            : ["#FF80DF", "#2b2b2b"]
    ), [isMovieFrenzyRatingMode]);

    const ratingTitle = ratingMode === "tmdb"
        ? t("movie.rating.scoreTmdb")
        : t("movie.rating.scoreMovieFrenzy");

    const activeRatingsCount = isMovieFrenzyRatingMode
        ? movieFrenzyRatingsCount
        : tmdbRatingsCount;

    const activeRatingSource = isMovieFrenzyRatingMode
        ? "MovieFrenzy"
        : "TMDB";

    const ratingCaption = t(
        activeRatingsCount === 0
            ? "movie.rating.noRatingsForSource"
            : activeRatingsCount === 1
                ? "movie.rating.basedOnRatings_one"
                : "movie.rating.basedOnRatings_other"
    )
        .replace("{{count}}", activeRatingsCount.toLocaleString(currentLanguage))
        .replace("{{source}}", activeRatingSource);


    const handleRatingModeChange = async (nextMode) => {
        if (!isValidRatingMode(nextMode)) return;

        setRatingMode(nextMode);

        if (!isLoggedIn || !userId) return;

        try {
            const userRef = doc(db, "users", userId);

            await updateDoc(userRef, {
                "preferences.ratingSource": nextMode,
            });
        } catch (error) {
            console.error("Erreur lors de la sauvegarde du mode de notation:", error);
        }
    };

    useEffect(() => {
        const loadRatingPreference = async () => {
            if (!isLoggedIn || !userId) {
                setRatingMode("tmdb");
                return;
            }

            try {
                const userRef = doc(db, "users", userId);
                const userSnap = await getDoc(userRef);

                if (!userSnap.exists()) return;

                const savedRatingSource = userSnap.data()?.preferences?.ratingSource;

                if (isValidRatingMode(savedRatingSource)) {
                    setRatingMode(savedRatingSource);
                }
            } catch (error) {
                console.error("Erreur lors du chargement du mode de notation:", error);
            }
        };

        loadRatingPreference();
    }, [isLoggedIn, userId]);

    useEffect(() => {
        const loadMovieFrenzyRating = async () => {
            if (!id) return;

            try {
                const ratingRef = doc(db, "movieRatings", String(id));
                const ratingSnap = await getDoc(ratingRef);

                if (!ratingSnap.exists()) {
                    setMovieFrenzyAverageRating(null);
                    setMovieFrenzyRatingsCount(0);
                    return;
                }

                const ratingData = ratingSnap.data();
                const averageRating = Number(ratingData.averageRating);
                const ratingsCount = Number(ratingData.ratingsCount || 0);

                if (Number.isFinite(averageRating) && ratingsCount > 0) {
                    setMovieFrenzyAverageRating(averageRating);
                    setMovieFrenzyRatingsCount(ratingsCount);
                } else {
                    setMovieFrenzyAverageRating(null);
                    setMovieFrenzyRatingsCount(0);
                }
            } catch (error) {
                console.error("Erreur lors du chargement de la note MovieFrenzy:", error);
                setMovieFrenzyAverageRating(null);
                setMovieFrenzyRatingsCount(0);
            }
        };

        loadMovieFrenzyRating();
    }, [id]);

    const cleanupOrphanMovieReviewReplies = useCallback(async (movieRatingKey, effectiveUserId) => {
        if (!movieRatingKey || !effectiveUserId) return 0;

        try {
            const orphanRepliesQuery = query(
                collectionGroup(db, "replies"),
                where("movieRatingKey", "==", String(movieRatingKey))
            );

            const orphanRepliesSnap = await getDocs(orphanRepliesQuery);

            const orphanReplyRefs = [];

            await Promise.all(
                orphanRepliesSnap.docs.map(async (replyDocSnap) => {
                    const replyData = replyDocSnap.data();

                    const isCurrentUserMovieReview =
                        replyData?.source === "movie_rating_review" &&
                        replyData?.authorId === effectiveUserId;

                    if (!isCurrentUserMovieReview) return;

                    const parentPostRef = replyDocSnap.ref.parent.parent;

                    if (!parentPostRef) {
                        orphanReplyRefs.push(replyDocSnap.ref);
                        return;
                    }

                    const parentPostSnap = await getDoc(parentPostRef);

                    if (!parentPostSnap.exists()) {
                        orphanReplyRefs.push(replyDocSnap.ref);
                    }
                })
            );

            await Promise.all(
                orphanReplyRefs.map((replyRef) => {
                    const parentPostRef = replyRef.parent.parent;

                    if (!parentPostRef || parentPostRef.parent.id !== "posts") {
                        throw new Error("Chemin d'avis orphelin inattendu.");
                    }

                    return deleteForumReply({
                        postId: parentPostRef.id,
                        replyId: replyRef.id,
                    });
                })
            );

            if (orphanReplyRefs.length > 0) {
                console.warn(
                    `[MovieFrenzy] ${orphanReplyRefs.length} avis MovieFrenzy orphelin(s) supprimé(s).`
                );
            }

            return orphanReplyRefs.length;
        } catch (error) {
            console.error(
                "[MovieFrenzy] Erreur pendant le nettoyage des avis orphelins :",
                error
            );

            return 0;
        }
    }, []);

    useEffect(() => {
        const loadUserMovieRating = async () => {
            const effectiveUserId = userId || auth.currentUser?.uid;

            if (!isLoggedIn || !effectiveUserId || !id) {
                setUserMovieRating(null);
                setMovieReviewMeta(null);
                return;
            }

            setIsUserRatingLoading(true);

            try {
                const movieRatingKey = String(id);
                await cleanupOrphanMovieReviewReplies(movieRatingKey, effectiveUserId);
                const userRatingRef = doc(
                    db,
                    "movieRatings",
                    movieRatingKey,
                    "userRatings",
                    effectiveUserId
                );

                const userRatingSnap = await getDoc(userRatingRef);

                if (!userRatingSnap.exists()) {
                    setUserMovieRating(null);
                    setMovieReviewMeta(null);
                    return;
                }

                const ratingData = userRatingSnap.data();
                const rating = Number(ratingData.rating);

                setUserMovieRating(Number.isFinite(rating) ? rating : null);

                const hasReview = ratingData.hasReview === true;
                const forumPostId = ratingData.forumPostId || null;
                const forumReplyId = ratingData.forumReplyId || null;

                if (!hasReview || !forumPostId || !forumReplyId) {
                    setMovieReviewMeta(null);
                    return;
                }

                const forumPostRef = doc(db, "posts", forumPostId);
                const forumReplyRef = doc(
                    db,
                    "posts",
                    forumPostId,
                    "replies",
                    forumReplyId
                );

                const [forumPostSnap, forumReplySnap] = await Promise.all([
                    getDoc(forumPostRef),
                    getDoc(forumReplyRef),
                ]);

                if (!forumPostSnap.exists() || !forumReplySnap.exists()) {
                    console.warn(
                        "[MovieFrenzy] Avis orphelin détecté. Nettoyage des métadonnées utilisateur."
                    );

                    await updateDoc(userRatingRef, {
                        hasReview: false,
                        forumPostId: deleteField(),
                        forumReplyId: deleteField(),
                        reviewText: deleteField(),
                        reviewUpdatedAt: deleteField(),
                    });

                    setMovieReviewMeta(null);
                    return;
                }

                const replyData = forumReplySnap.data() || {};
                const reviewTextFromReply = extractReviewTextFromForumContent(
                    replyData.content || ""
                );

                setMovieReviewMeta({
                    hasReview: true,
                    forumPostId,
                    forumReplyId,
                    reviewText: reviewTextFromReply || ratingData.reviewText || "",
                });
            } catch (error) {
                console.error("Erreur lors du chargement de la note utilisateur:", error);
                setUserMovieRating(null);
                setMovieReviewMeta(null);
            } finally {
                setIsUserRatingLoading(false);
            }
        };
        loadUserMovieRating();
    }, [isLoggedIn, userId, id, cleanupOrphanMovieReviewReplies]);

    useEffect(() => {
        if (userMovieRating !== null) {
            setSelectedUserMovieRating(userMovieRating);
            setUserRatingMessage("");
        }
    }, [userMovieRating]);

    const videoOptions = {
        height: '390',
        width: '640',
        playerVars: {
            autoplay: 1,
            color: "white",
            controls: 1,
            modestbranding: 1,
            rel: 0,
            fs: 1,

            // Langue de l’interface interne du lecteur YouTube.
            hl: currentLanguage,

            // Langue préférée si le spectateur active lui-même les sous-titres.
            cc_lang_pref: currentLanguage,

            // Laisser désactivé pour ne pas forcer les sous-titres au démarrage.
            // cc_load_policy: 1,

            iv_load_policy: 3,
            enablejsapi: 1,
            origin: window.location.origin,
        }
    };

    const resolveKinoFallback = useCallback(async (candidates, videoLoad) => {
        if (
            videoLoadRef.current !== videoLoad ||
            candidates.trailer.length > 0 ||
            videoLoad.kinoAttempted
        ) {
            return candidates;
        }

        videoLoad.kinoAttempted = true;
        videoLoad.kinoPending = true;
        setIsKinoLoading(true);

        try {
            const videos = await fetchKinoCheckTrailers({
                tmdbId: videoLoad.movieId,
                language: videoLoad.language,
            });

            if (videoLoadRef.current !== videoLoad) return candidates;

            // Éviter les vidéos déjà rejetées ou déjà présentes
            // dans une autre catégorie, notamment les Bonus.
            const excludedKeys = new Set([
                ...rejectedVideoKeysRef.current,
                ...Object.values(candidates).flat().map((video) => video.key),
            ]);

            const availableVideos = videos.filter(
                (video) => !excludedKeys.has(video.key)
            );

            const ranked = rankVideoCandidates(
                availableVideos,
                videoLoad.language
            );

            return { ...candidates, trailer: ranked.main };
        } finally {
            videoLoad.kinoPending = false;

            if (videoLoadRef.current === videoLoad) {
                setIsKinoLoading(false);
            }
        }
    }, []);

    const _onReady = (event) => {
        event.target.playVideo();
    };

    const _onError = async (event) => {
        const videoLoad = videoLoadRef.current;

        if (
            !videoLoad ||
            videoLoad.movieId !== id ||
            videoLoad.language !== currentLanguage ||
            videoLoad.kinoPending
        ) {
            return;
        }

        const reportedVideoKey = event.target?.getVideoData?.()?.video_id;
        const failedVideoKey = reportedVideoKey || videoKey;

        console.warn("[MovieFrenzy YouTube]", {
            errorCode: event.data,
            videoKey: failedVideoKey,
        });

        if (
            ![5, 100, 101, 150].includes(event.data) ||
            !failedVideoKey ||
            failedVideoKey !== videoKey ||
            !videoCandidates.some((video) => video.key === failedVideoKey) ||
            rejectedVideoKeysRef.current.has(failedVideoKey)
        ) {
            return;
        }

        rejectedVideoKeysRef.current.add(failedVideoKey);

        const remainingCandidates = Object.fromEntries(
            videoCategoryOrder.map((category) => [
                category,
                videoCandidatesByCategory[category].filter(
                    (video) => !rejectedVideoKeysRef.current.has(video.key)
                ),
            ])
        );

        setVideoKey("");
        setVideoCandidatesByCategory(remainingCandidates);

        const nextCandidates = await resolveKinoFallback(
            remainingCandidates,
            videoLoad
        );

        if (videoLoadRef.current !== videoLoad) return;

        const nextCategory =
            nextCandidates[activeVideoCategory].length > 0
                ? activeVideoCategory
                : videoCategoryOrder.find(
                    (category) => nextCandidates[category].length > 0
                ) || "trailer";

        setVideoCandidatesByCategory(nextCandidates);
        setActiveVideoCategory(nextCategory);
        setVideoKey(nextCandidates[nextCategory][0]?.key || "");
    };

    const handleVideoCategoryChange = (category) => {
        if (videoLoadRef.current?.kinoPending) return;

        // La suite actuelle de ta fonction...
        if (category === activeVideoCategory) {
            return;
        }

        const categoryCandidates =
            videoCandidatesByCategory[category] || [];

        if (categoryCandidates.length === 0) {
            return;
        }

        const firstAvailableVideo = categoryCandidates.find(
            (candidate) =>
                !rejectedVideoKeysRef.current.has(candidate.key)
        );

        setActiveVideoCategory(category);
        setVideoKey(firstAvailableVideo?.key || "");
    };

    const handleActeurClick = (acteurId) => {
        history(`/acteurDetails/${acteurId}`);
    };

    const handleCategoryClick = (categoryId) => {
        history(`/categories/${categoryId}`);
    };



    // Fonction mémorisée pour récupérer les détails du film, les crédits et la bande-annonce
    const fetchAllMovieDetails = useCallback(async () => {
        const videoLoad = {
            movieId: id,
            language: currentLanguage,
            kinoAttempted: false,
            kinoPending: false,
        };

        videoLoadRef.current = videoLoad;
        rejectedVideoKeysRef.current = new Set();
        setIsKinoLoading(false);
        setVideoKey("");
        setActiveVideoCategory("trailer");
        setVideoCandidatesByCategory({
            trailer: [],
            behindScenes: [],
            bonus: [],
        });
        try {
            const tmdbLocale = getTmdbLocale(currentLanguage);

            if (!TMDB_API_KEY) {
                throw new Error(
                    "La variable REACT_APP_TMDB_API_KEY est absente."
                );
            }

            setMaturityRating(null);
            setQuebecProductionInfo(null);
            setOmdbData(null);
            const [
                detailResponse,
                creditsResponse,
                videoResponse,
                ageRatingResponse
            ] = await Promise.all([
                axios.get(
                    `https://api.themoviedb.org/3/movie/${id}?api_key=${TMDB_API_KEY}&language=${tmdbLocale}`
                ),
                axios.get(
                    `https://api.themoviedb.org/3/movie/${id}/credits?api_key=${TMDB_API_KEY}&language=${tmdbLocale}`
                ),
                axios.get(
                    `https://api.themoviedb.org/3/movie/${id}/videos?api_key=${TMDB_API_KEY}&language=${tmdbLocale}`
                ),
                axios.get(
                    `https://api.themoviedb.org/3/movie/${id}/release_dates?api_key=${TMDB_API_KEY}&language=${tmdbLocale}`
                )
            ]);

            let movieDetails = detailResponse.data;
            const movieCredits = creditsResponse.data;
            const movieVideos = videoResponse.data.results;
            const movieAgeRatings = ageRatingResponse.data.results;

            /*
 * Lorsqu’aucun synopsis n’existe dans la langue choisie,
 * tenter uniquement le synopsis anglais sans remplacer
 * les autres renseignements localisés du film.
 */
            if (
                !String(movieDetails.overview || "").trim() &&
                tmdbLocale !== "en-US"
            ) {
                try {
                    const englishDetailResponse = await axios.get(
                        `https://api.themoviedb.org/3/movie/${id}?api_key=${TMDB_API_KEY}&language=en-US`
                    );

                    movieDetails = {
                        ...movieDetails,
                        overview: String(
                            englishDetailResponse.data?.overview || ""
                        ).trim(),
                    };
                } catch (error) {
                    console.warn(
                        "[MovieFrenzy] Synopsis anglais de relève indisponible.",
                        error
                    );
                }
            }

            if (videoLoadRef.current !== videoLoad) return;

            // Logique pour mettre à jour l'état avec les détails du film
            setDetail(movieDetails);
            setGenres(movieDetails.genres);
            setActors(movieCredits.cast);

            const omdbRequest = fetchOmdbMovieData({
                imdbId: movieDetails.imdb_id,

                title:
                    movieDetails.original_title ||
                    movieDetails.title,

                year:
                    movieDetails.release_date?.slice(0, 4),
            });

            // Ordre de priorité : langue choisie,
            // langue originale du film, puis anglais.
            const languagePriority = [
                currentLanguage,
                movieDetails.original_language,
                "en",
            ].filter((language, index, languages) => {
                return (
                    Boolean(language) &&
                    languages.indexOf(language) === index
                );
            });

            // Récupérer et classer tous les candidats pour permettre
            // une relève automatique lorsqu’une vidéo refuse l’intégration.
            const candidateGroups = await Promise.all(
                languagePriority.map(async (candidateLanguage) => {
                    try {
                        let candidateVideos = movieVideos;

                        // La première réponse a déjà été obtenue dans Promise.all.
                        // On appelle TMDB uniquement pour les langues de repli.
                        if (candidateLanguage !== currentLanguage) {
                            const candidateLocale =
                                getTmdbLocale(candidateLanguage);

                            const fallbackVideoResponse = await axios.get(
                                `https://api.themoviedb.org/3/movie/${id}/videos?api_key=${TMDB_API_KEY}&language=${candidateLocale}`
                            );

                            candidateVideos =
                                fallbackVideoResponse.data.results;
                        }

                        const rankedCandidates = rankVideoCandidates(
                            candidateVideos,
                            candidateLanguage
                        );

                        const addCandidateMetadata = (
                            videos,
                            candidateLevel
                        ) => {
                            return videos.map((video) => ({
                                ...video,
                                movieFrenzyLanguage: candidateLanguage,
                                movieFrenzyCandidateLevel: candidateLevel,
                            }));
                        };

                        return {
                            main: addCandidateMetadata(
                                rankedCandidates.main,
                                "main"
                            ),

                            backup: addCandidateMetadata(
                                rankedCandidates.backup,
                                "backup"
                            ),

                            behindScenes: addCandidateMetadata(
                                rankedCandidates.behindScenes,
                                "standard"
                            ),

                            bonus: addCandidateMetadata(
                                rankedCandidates.bonus,
                                "standard"
                            ),
                        };
                    } catch (error) {
                        console.warn(
                            `[MovieFrenzy] Impossible de récupérer les vidéos ${candidateLanguage}`,
                            error
                        );

                        return {
                            main: [],
                            backup: [],
                            behindScenes: [],
                            bonus: [],
                        };
                    }
                })
            );

            // Récupérer les vidéos de relève propres à MovieFrenzy.
            // Elles passeront après toutes les vidéos obtenues auprès de TMDB.
            const rankedMovieFrenzyFallbacks =
                rankVideoCandidates(
                    getMovieVideoFallbacks(id),
                    currentLanguage
                );

            const addFallbackMetadata = (videos = []) => {
                return videos.map((video) => ({
                    ...video,
                    movieFrenzyLanguage:
                        video.iso_639_1 || currentLanguage,

                    movieFrenzyCandidateLevel: "fallback",
                }));
            };

            const movieFrenzyFallbackGroup = {
                main: addFallbackMetadata(
                    rankedMovieFrenzyFallbacks.main
                ),

                backup: addFallbackMetadata(
                    rankedMovieFrenzyFallbacks.backup
                ),

                behindScenes: addFallbackMetadata(
                    rankedMovieFrenzyFallbacks.behindScenes
                ),

                bonus: addFallbackMetadata(
                    rankedMovieFrenzyFallbacks.bonus
                ),
            };

            // Les groupes TMDB restent prioritaires.
            // Le groupe MovieFrenzy intervient en dernier recours.
            const allCandidateGroups = [
                ...candidateGroups,
                movieFrenzyFallbackGroup,
            ];

            const removeDuplicateVideos = (videos = []) => {
                return videos.filter(
                    (video, index, allVideos) =>
                        allVideos.findIndex(
                            (candidate) =>
                                candidate.key === video.key
                        ) === index
                );
            };

            const mainTrailerCandidates =
                allCandidateGroups.flatMap(
                    (group) => group.main
                );

            const backupTrailerCandidates =
                allCandidateGroups.flatMap(
                    (group) => group.backup
                );

            const candidatesByCategory = {
                trailer: removeDuplicateVideos([
                    ...mainTrailerCandidates,
                    ...backupTrailerCandidates,
                ]),

                behindScenes: removeDuplicateVideos(
                    allCandidateGroups.flatMap(
                        (group) => group.behindScenes
                    )
                ),

                bonus: removeDuplicateVideos(
                    allCandidateGroups.flatMap(
                        (group) => group.bonus
                    )
                ),
            };

            const resolvedCandidates = await resolveKinoFallback(
                candidatesByCategory,
                videoLoad
            );

            if (videoLoadRef.current !== videoLoad) return;

            const initialVideoCategory =
                ["trailer", "behindScenes", "bonus"].find(
                    (category) => resolvedCandidates[category].length > 0
                ) || "trailer";

            setVideoCandidatesByCategory(resolvedCandidates);
            setActiveVideoCategory(initialVideoCategory);
            setVideoKey(
                resolvedCandidates[initialVideoCategory][0]?.key || ""
            );


            // ======================================================
            // PERSONNALITÉS — RÉALISATEURS ET SCÉNARISTE
            // ======================================================

            const movieCrew = Array.isArray(movieCredits.crew)
                ? movieCredits.crew
                : [];

            // Un film peut posséder plusieurs réalisateurs.
            // On conserve leurs objets TMDB complets, notamment leur id.
            const movieDirectors = movieCrew
                .filter(
                    (crewMember) =>
                        crewMember.job?.toLowerCase() === "director"
                )
                .filter(
                    (director, index, allDirectors) =>
                        allDirectors.findIndex(
                            (candidate) =>
                                candidate.id === director.id
                        ) === index
                );

            setDirectors(movieDirectors);

            // ======================================================
            // ORIGINE QUÉBÉCOISE DU FILM
            // ======================================================

            const nextQuebecProductionInfo =
                await findQuebecProduction({
                    tmdbId: movieDetails.id,

                    title:
                        movieDetails.title,

                    originalTitle:
                        movieDetails.original_title,

                    releaseDate:
                        movieDetails.release_date,

                    directors:
                        movieDirectors,
                });

            setQuebecProductionInfo(
                nextQuebecProductionInfo
            );

            // Pour le moment, MovieFrenzy conserve un seul scénariste.
            const writer = movieCrew.find(
                (crewMember) =>
                    crewMember.job?.toLowerCase() === "screenplay" ||
                    crewMember.job?.toLowerCase() === "writer"
            );

            setDetail((prevDetail) => ({
                ...prevDetail,
                writer: writer?.name || "",
            }));

            // ======================================================
            // CLASSEMENT DE MATURITÉ
            // ======================================================

            let nextMaturityRating = null;

            /*
             * Lorsqu’un membre réside au Québec, MovieFrenzy
             * cherche d’abord le classement officiel québécois.
             */
            if (ratingSystem === "QC") {
                const officialQuebecRating =
                    await findQuebecRating({
                        tmdbId: movieDetails.id,
                        title: movieDetails.title,
                        originalTitle:
                            movieDetails.original_title,
                        releaseDate:
                            movieDetails.release_date,
                        directors: movieDirectors,
                    });

                if (officialQuebecRating) {
                    const officialCode =
                        normalizeCertificationCode(
                            officialQuebecRating.rating
                        );

                    nextMaturityRating = {
                        system: "QC",
                        sourceCountry: "QC",
                        source: "quebec-official",

                        code: officialCode,

                        label: formatCertificationLabel({
                            system: "QC",
                            code: officialCode,
                            t,
                        }),

                        advisories:
                            officialQuebecRating.advisories || [],

                        nations:
                            officialQuebecRating.nations || [],

                        sourceUrl:
                            officialQuebecRating.sourceUrl || "",

                        quebecFilmId:
                            officialQuebecRating.quebecFilmId ||
                            null,
                    };
                }
            }

            /*
             * Si le film n’est pas retrouvé dans l’index québécois,
             * conserver le système de repli déjà en place avec TMDB.
             */
            if (!nextMaturityRating) {
                nextMaturityRating =
                    buildMaturityRating({
                        movieAgeRatings,
                        ratingSystem,
                        language: currentLanguage,
                        t,
                    });
            }

            setMaturityRating(nextMaturityRating);

            const omdbMovieData =
                await omdbRequest;

            setOmdbData(omdbMovieData);

        } catch (error) {
            console.log(error);
        }
    }, [
        id,
        currentLanguage,
        ratingSystem,
        t,
        resolveKinoFallback,
    ]);// Les dépendances de useCallback

    // Utilisation de la fonction mémorisée comme dépendance de useEffect
    useEffect(() => {
        fetchAllMovieDetails();

        return () => {
            videoLoadRef.current = null;
        };
    }, [fetchAllMovieDetails]);


    useEffect(() => {
        const checkFavoriteStatus = async () => {
            if (isLoggedIn && userId && detail.id) {
                try {
                    const userRef = doc(db, "users", userId);
                    const userSnap = await getDoc(userRef);
                    if (userSnap.exists()) {
                        const userData = userSnap.data();
                        const currentFavorites = userData.favoritePreviews || [];
                        if (currentFavorites.some(movie => movie.id === detail.id)) {
                            // setIsFavorite(true); // This line was removed
                        }
                    }
                } catch (error) {
                    console.error("Error checking favorite status:", error);
                }
            }
        };
        checkFavoriteStatus();
    }, [isLoggedIn, userId, detail.id]);


    const renderGenres = (genreIds) => {
        return genreIds.map((id) => {
            const genre = genres.find((g) => g.id === id);
            if (genre) {
                return <Genre key={id} id={genre.id} name={genre.name} />;
            }
            return null;
        });
    };


    // Doughnut chart data and options
    useEffect(() => {
        setChartData({
            datasets: [
                {
                    data: [activeChartPercent, 100 - activeChartPercent],
                    backgroundColor: activeChartColors,
                    borderWidth: 0,
                },
            ],
        });
    }, [activeChartPercent, activeChartColors]);

    const chartOptions = {
        plugins: {
            legend: {
                display: false,
            },
            tooltip: {
                enabled: activeRatingPercent !== null,
            },
        },
        onHover: (event, chartElement, chart) => {
            // Restore original styles if no element is hovered
            if (chartElement.length === 0) {
                chart.data.datasets[0].backgroundColor = activeChartColors;
                setChartTextStyle({});
                chart.update();
                return;
            }

            const element = chartElement[0];
            // If hovering over the pink part (index 0)
            if (element.index === 0) {
                chart.data.datasets[0].backgroundColor = activeChartHoverColors;
                setChartTextStyle({
                    // transform: 'scale(1.1)',
                    color: '#00FFFF', // Cyan color
                });
            } else {
                // Optional: handle hover on the grey part
                chart.data.datasets[0].backgroundColor = activeChartColors;
                setChartTextStyle({});
            }
            chart.update();
        },
        animation: {
            duration: isVisible ? 1000 : 0,
        },
    };

    useEffect(() => {
        window.scrollTo(0, 0);

        const chartElement =
            document.querySelector(".chart-container");

        const overviewSectionElement =
            overviewSectionRef.current;

        /*
         * Solution de secours pour les navigateurs
         * ne prenant pas en charge IntersectionObserver.
         */
        if (!("IntersectionObserver" in window)) {
            setIsVisible(true);

            overviewSectionElement?.classList.add(
                "is-overview-visible"
            );

            return;
        }

        const chartObserver = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setIsVisible(true);
                        chartObserver.unobserve(entry.target);
                    }
                });
            },
            {
                threshold: 1,
            }
        );

        const overviewObserver = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (!entry.isIntersecting) {
                        return;
                    }

                    entry.target.classList.add(
                        "is-overview-visible"
                    );

                    overviewObserver.unobserve(entry.target);
                });
            },
            {
                threshold: 0.3,
                rootMargin: "0px 0px -8% 0px",
            }
        );

        if (chartElement) {
            chartObserver.observe(chartElement);
        }

        if (overviewSectionElement) {
            /*
             * Permet de rejouer l’entrée lorsqu’on change
             * de film sans démonter complètement la page.
             */
            overviewSectionElement.classList.remove(
                "is-overview-visible"
            );

            overviewObserver.observe(
                overviewSectionElement
            );
        }

        return () => {
            chartObserver.disconnect();
            overviewObserver.disconnect();
        };
    }, [id]);

    useEffect(() => {
        if (!isVisible || activeRatingPercent === null || activeChartPercent <= 0) {
            setAnimatedPercent("0.00");
            return;
        }

        let start = null;
        const duration = 1000;

        const step = (timestamp) => {
            if (!start) start = timestamp;
            const progress = Math.min((timestamp - start) / duration, 1);
            const currentPercent = progress * activeChartPercent;
            setAnimatedPercent(currentPercent.toFixed(2));

            if (progress < 1) {
                window.requestAnimationFrame(step);
            } else {
                setAnimatedPercent(Number(activeRatingPercent).toFixed(2));
            }
        };

        window.requestAnimationFrame(step);
    }, [isVisible, activeRatingPercent, activeChartPercent]);


    const addFavoritePreview = async () => {
        try {
            if (!isLoggedIn || !detail) return;

            const effectiveUserId = auth.currentUser?.uid;

            if (!effectiveUserId) {
                console.error("Aucun utilisateur connecté.");
                return;
            }

            const userRef = doc(db, "users", effectiveUserId);
            const userSnap = await getDoc(userRef);

            if (!userSnap.exists()) {
                console.error("No such user!");
                return;
            }

            const userData = userSnap.data();
            const currentFavorites = userData.favoritePreviews || [];

            if (currentFavorites.some(movie => movie.id === detail.id)) {
                setShowAlreadyInFavorites(true);
                setTimeout(() => {
                    setShowAlreadyInFavorites(false);
                }, 3000);
                return;
            }

            if (currentFavorites.length >= 3) {
                setShowLimitMessage(true);
                setTimeout(() => {
                    setShowLimitMessage(false);
                }, 3000);
                return;
            }

            const newFavoritePreviews = [...currentFavorites, detail];
            const nextFavoriteMoviesCount = Math.min(newFavoritePreviews.length, 3);

            await updateDoc(userRef, {
                favoritePreviews: newFavoritePreviews,
                "xp.sources.favoriteMovies": nextFavoriteMoviesCount,
                "xp.updatedAt": serverTimestamp(),
            });

            setActivityGainToast({
                id: `favorite-movie-${detail.id}-${Date.now()}`,
                points: "+1.5",
                unitLabel: t("profilMember.activity"),
                label: "",
                variant: "favorite",
            });

            setShowConfirmation(true);
            setTimeout(() => {
                setShowConfirmation(false);
            }, 3000);
        } catch (error) {
            console.error("Error adding favorite preview:", error);
        }
    };

    const handleSaveUserMovieRating = async () => {
        const effectiveUserId = userId || auth.currentUser?.uid;

        if (!isLoggedIn || !effectiveUserId || !id) {
            setUserRatingMessage(t("movie.rating.loginToSaveRating"));
            return;
        }

        const rating = Number(selectedUserMovieRating);

        if (!Number.isFinite(rating) || rating < 1 || rating > 10) {
            setUserRatingMessage(t("movie.rating.invalidRating"));
            return;
        }

        setIsSavingUserRating(true);
        setUserRatingMessage("");

        try {
            const movieRatingRef = doc(db, "movieRatings", String(id));
            const userRatingRef = doc(
                db,
                "movieRatings",
                String(id),
                "userRatings",
                effectiveUserId
            );

            const userRef = doc(db, "users", effectiveUserId);

            const result = await runTransaction(db, async (transaction) => {
                const movieRatingSnap = await transaction.get(movieRatingRef);
                const userRatingSnap = await transaction.get(userRatingRef);

                const movieRatingData = movieRatingSnap.exists()
                    ? movieRatingSnap.data()
                    : {};

                const previousRating = userRatingSnap.exists()
                    ? Number(userRatingSnap.data().rating)
                    : null;

                const hasPreviousRating = Number.isFinite(previousRating);

                const currentTotal = Number(movieRatingData.totalRating || 0);
                const currentCount = Number(movieRatingData.ratingsCount || 0);

                let nextTotal;
                let nextCount;

                if (hasPreviousRating && currentCount <= 1) {
                    nextTotal = rating;
                    nextCount = 1;
                } else if (hasPreviousRating && currentCount > 1) {
                    nextTotal = currentTotal - previousRating + rating;
                    nextCount = currentCount;
                } else {
                    nextTotal = currentTotal + rating;
                    nextCount = currentCount + 1;
                }

                const safeTotal = Number(nextTotal.toFixed(2));
                const nextAverage = Number((safeTotal / nextCount).toFixed(2));

                transaction.set(
                    movieRatingRef,
                    {
                        averageRating: nextAverage,
                        ratingsCount: nextCount,
                        totalRating: safeTotal,
                        tmdbId: Number(id),
                        title: detail.title || movieRatingData.title || "",
                        updatedAt: serverTimestamp(),
                    },
                    { merge: true }
                );

                transaction.set(
                    userRatingRef,
                    {
                        rating,
                        updatedAt: serverTimestamp(),
                        ...(hasPreviousRating ? {} : { createdAt: serverTimestamp() }),
                    },
                    { merge: true }
                );
                if (!hasPreviousRating) {
                    transaction.update(userRef, {
                        "xp.sources.movieRatings": increment(1),
                        "xp.updatedAt": serverTimestamp(),
                    });
                }

                return {
                    averageRating: nextAverage,
                    ratingsCount: nextCount,
                    totalRating: safeTotal,
                    isFirstRating: !hasPreviousRating,
                };
            });

            setUserMovieRating(rating);
            setMovieFrenzyAverageRating(result.averageRating);
            setMovieFrenzyRatingsCount(result.ratingsCount);
            setUserRatingMessage(
                t("movie.rating.ratingSaved").replace("{{rating}}", rating)
            );
            if (result.isFirstRating) {
                setActivityGainToast({
                    id: `movie-rating-${id}-${Date.now()}`,
                    points: "+0.5",
                    unitLabel: t("profilMember.activity"),
                    label: t("activityToast.movieRatingFirst"),
                    variant: "rating",
                });
            }
        } catch (error) {
            console.error("Erreur lors de l’enregistrement de la note utilisateur:", error);
            setUserRatingMessage(t("movie.rating.ratingSaveError"));
        } finally {
            setIsSavingUserRating(false);
        }
    };

    const handleViewMovieReview = () => {
        if (!movieReviewMeta?.forumPostId) return;

        history(`/subject/${movieReviewMeta.forumPostId}`);
    };

    const handleOpenMovieReview = async () => {
        let initialDraft = "";

        if (
            hasExistingMovieReview &&
            movieReviewMeta?.forumPostId &&
            movieReviewMeta?.forumReplyId
        ) {
            try {
                const existingReplyRef = doc(
                    db,
                    "posts",
                    movieReviewMeta.forumPostId,
                    "replies",
                    movieReviewMeta.forumReplyId
                );

                const existingReplySnap = await getDoc(existingReplyRef);

                if (existingReplySnap.exists()) {
                    const existingReplyData = existingReplySnap.data();

                    initialDraft = extractReviewTextFromForumContent(
                        existingReplyData.content || ""
                    );
                } else {
                    initialDraft = movieReviewMeta?.reviewText || "";
                }
            } catch (error) {
                console.error(
                    "[MovieFrenzy] Erreur lors du chargement de l'avis existant :",
                    error
                );

                initialDraft = movieReviewMeta?.reviewText || "";
            }
        }

        setMovieReviewDraft(initialDraft);
        setIsMovieReviewModalOpen(true);

        console.log("[MovieFrenzy] Ouvrir le modal d'avis", {
            movieId: id,
            title: detail.title,
            userRating: userMovieRating,
            hasExistingMovieReview,
            initialDraftLength: initialDraft.length,
        });
    };

    const handleDeleteMovieReview = async () => {
        const effectiveUserId = userId || auth.currentUser?.uid;
        const forumPostId = movieReviewMeta?.forumPostId;
        const forumReplyId = movieReviewMeta?.forumReplyId;

        if (!effectiveUserId || !id || !forumPostId || !forumReplyId) {
            console.warn(
                "Suppression impossible : métadonnées d'avis MovieFrenzy incomplètes."
            );
            return;
        }

        const confirmed = window.confirm(
            t("movie.rating.deleteReviewConfirm")
        );

        if (!confirmed) return;

        setIsDeletingMovieReview(true);

        try {
            const result = await deleteForumReply({
                postId: forumPostId,
                replyId: forumReplyId,
            });

            const deletedForumPost = result.postDeleted === true;

            setMovieReviewMeta(null);
            setMovieReviewDraft("");

            if (deletedForumPost) {
                setMovieReviewForumPostId(null);
                setMovieReviewRepliesCount(0);
            } else if (result.deleted) {
                setMovieReviewRepliesCount((count) =>
                    Math.max(0, Number(count || 0) - 1)
                );
            }

            setIsMovieReviewModalOpen(false);
        } catch (error) {
            console.error(
                "Erreur lors de la suppression de l'avis MovieFrenzy :",
                error
            );
        } finally {
            setIsDeletingMovieReview(false);
        }
    };

    const handleCloseMovieReview = () => {
        setIsMovieReviewModalOpen(false);
        setMovieReviewDraft("");
    };

    const handleSubmitMovieReviewDraft = async () => {
        const trimmedReview = movieReviewDraft.trim();
        const effectiveUserId = userId || auth.currentUser?.uid;

        if (trimmedReview.length < 3) {
            return;
        }

        if (!isLoggedIn || !effectiveUserId || !id) {
            console.error("[MovieFrenzy] Impossible de publier l'avis : utilisateur ou film manquant.");
            return;
        }

        try {
            const movieRatingKey = String(id);
            const movieTitle = detail.title || "ce film";

            const userRatingRef = doc(
                db,
                "movieRatings",
                movieRatingKey,
                "userRatings",
                effectiveUserId
            );

            const userRef = doc(db, "users", effectiveUserId);

            const profileReviewRef = doc(
                db,
                "users",
                effectiveUserId,
                "movieReviews",
                movieRatingKey
            );

            const buildProfileReviewData = ({ forumPostId, forumReplyId }) => ({
                tmdbId: Number(id),
                movieRatingKey,
                movieTitle,
                moviePosterPath: detail.poster_path || "",
                movieBackdropPath: detail.backdrop_path || "",
                rating: Number(userMovieRating),
                reviewText: trimmedReview,
                reviewExcerpt: createMovieReviewExcerpt(trimmedReview),
                forumPostId,
                forumReplyId,
                authorId: effectiveUserId,
                source: "movie_rating_review",
                updatedAt: serverTimestamp(),
            });

            if (hasExistingMovieReview) {
                const existingReplyRef = doc(
                    db,
                    "posts",
                    movieReviewMeta.forumPostId,
                    "replies",
                    movieReviewMeta.forumReplyId
                );
                const existingForumPostRef = doc(
                    db,
                    "posts",
                    movieReviewMeta.forumPostId
                );

                const profileReviewSnap = await getDoc(profileReviewRef);
                const updateBatch = writeBatch(db);

                updateBatch.update(existingReplyRef, {
                    content: formatMovieReviewContent({
                        movieTitle,
                        rating: userMovieRating,
                        review: trimmedReview,
                    }),
                    movieRating: Number(userMovieRating),
                    movieTitle,
                    updatedAt: serverTimestamp(),
                });

                updateBatch.update(existingForumPostRef, {
                    moviePosterPath: detail.poster_path || "",
                    movieBackdropPath: detail.backdrop_path || "",
                    movieTitle,
                    tmdbId: Number(id),
                    movieRatingKey,
                    source: "movie_rating_review",
                    type: "movie_rating_discussion",
                });

                updateBatch.update(userRatingRef, {
                    reviewText: trimmedReview,
                    reviewUpdatedAt: serverTimestamp(),
                });

                updateBatch.set(
                    profileReviewRef,
                    {
                        ...buildProfileReviewData({
                            forumPostId: movieReviewMeta.forumPostId,
                            forumReplyId: movieReviewMeta.forumReplyId,
                        }),

                        ...(profileReviewSnap.exists()
                            ? {}
                            : {
                                createdAt: serverTimestamp(),
                                visibleOnProfile: true,
                            }),
                    },
                    { merge: true }
                );

                await updateBatch.commit();

                setMovieReviewMeta((prevMeta) => ({
                    ...prevMeta,
                    reviewText: trimmedReview,
                }));

                console.log("[MovieFrenzy] Avis MovieFrenzy modifié", {
                    forumPostId: movieReviewMeta.forumPostId,
                    forumReplyId: movieReviewMeta.forumReplyId,
                    movieRatingKey,
                    movieTitle,
                });

                setIsMovieReviewModalOpen(false);
                setMovieReviewDraft("");
                history(`/subject/${movieReviewMeta.forumPostId}`);
                return;
            }

            let forumPostId = null;

            const existingForumTopicQuery = query(
                collection(db, "posts"),
                where("movieRatingKey", "==", movieRatingKey),
                limit(1)
            );

            const existingForumTopicSnap = await getDocs(existingForumTopicQuery);

            if (!existingForumTopicSnap.empty) {
                const existingForumTopicDoc = existingForumTopicSnap.docs[0];
                const existingForumTopicData = existingForumTopicDoc.data();

                forumPostId = existingForumTopicDoc.id;

                if (!existingForumTopicData.moviePosterPath && detail.poster_path) {
                    await updateDoc(doc(db, "posts", forumPostId), {
                        moviePosterPath: detail.poster_path || "",
                        movieBackdropPath: detail.backdrop_path || "",
                        movieTitle,
                        tmdbId: Number(id),
                    });
                }
            } else {
                const forumTopicRef = await addDoc(collection(db, "posts"), {
                    title: `Avis des cinéphiles sur ${movieTitle}`,
                    category: "autre",
                    content: `<p>Discussion ouverte pour les avis MovieFrenzy sur <strong>${escapeHtml(movieTitle)}</strong>.</p>`,
                    timestamp: serverTimestamp(),
                    author: username || "Anonyme",
                    authorId: effectiveUserId,
                    replies: 0,
                    likes: [],
                    source: "movie_rating_review",
                    type: "movie_rating_discussion",
                    tmdbId: Number(id),
                    movieRatingKey,
                    movieTitle,
                    moviePosterPath: detail.poster_path || "",
                    movieBackdropPath: detail.backdrop_path || "",
                });

                forumPostId = forumTopicRef.id;
            }

            const replyRef = doc(collection(db, "posts", forumPostId, "replies"));
            const forumPostRef = doc(db, "posts", forumPostId);

            const reviewBatch = writeBatch(db);

            reviewBatch.set(replyRef, {
                author: username || "Anonyme",
                authorId: effectiveUserId,
                content: formatMovieReviewContent({
                    movieTitle,
                    rating: userMovieRating,
                    review: trimmedReview,
                }),
                timestamp: serverTimestamp(),
                likes: [],
                source: "movie_rating_review",
                tmdbId: Number(id),
                movieRatingKey,
                movieTitle,
                movieRating: Number(userMovieRating),
            });

            reviewBatch.update(forumPostRef, {
                replies: increment(1),
                lastActivityAt: serverTimestamp(),
                lastActivityBy: effectiveUserId,
                lastActivityType: "movie_review",
            });

            reviewBatch.update(userRatingRef, {
                hasReview: true,
                forumPostId,
                forumReplyId: replyRef.id,
                reviewText: trimmedReview,
                reviewUpdatedAt: serverTimestamp(),
            });

            reviewBatch.update(userRef, {
                "xp.sources.movieReviews": increment(1),
                "xp.updatedAt": serverTimestamp(),
            });

            reviewBatch.set(profileReviewRef, {
                ...buildProfileReviewData({
                    forumPostId,
                    forumReplyId: replyRef.id,
                }),
                createdAt: serverTimestamp(),
                visibleOnProfile: true,
            });

            await reviewBatch.commit();

            setMovieReviewMeta({
                hasReview: true,
                forumPostId,
                forumReplyId: replyRef.id,
                reviewText: trimmedReview,
            });

            setActivityGainToast({
                id: `movie-review-${id}-${Date.now()}`,
                points: "+4",
                unitLabel: t("profilMember.activity"),
                label: t("activityToast.movieReviewFirst"),
                variant: "review",
            });

            console.log("[MovieFrenzy] Avis publié dans le Forum", {
                forumPostId,
                forumReplyId: replyRef.id,
                movieRatingKey,
                movieTitle,
            });

            setIsMovieReviewModalOpen(false);
            setMovieReviewDraft("");
            setMovieReviewForumPostId(forumPostId);

            setTimeout(() => {
                history(`/subject/${forumPostId}`);
            }, 1200);
        } catch (error) {
            console.error("[MovieFrenzy] Erreur lors de la publication/modification de l'avis Forum :", error);
        }
    };

    useEffect(() => {
        const loadMovieReviewDiscussion = async () => {
            if (!id) return;

            try {
                const movieRatingKey = String(id);

                const reviewTopicQuery = query(
                    collection(db, "posts"),
                    where("movieRatingKey", "==", movieRatingKey),
                    limit(1)
                );

                const reviewTopicSnap = await getDocs(reviewTopicQuery);

                if (reviewTopicSnap.empty) {
                    setMovieReviewForumPostId(null);
                    setMovieReviewRepliesCount(0);
                    return;
                }

                const reviewTopicDoc = reviewTopicSnap.docs[0];
                const reviewTopicData = reviewTopicDoc.data();

                setMovieReviewForumPostId(reviewTopicDoc.id);
                setMovieReviewRepliesCount(Number(reviewTopicData.replies) || 0);

            } catch (error) {
                console.error("[MovieFrenzy] Erreur lors du chargement du sujet d'avis :", error);
                setMovieReviewForumPostId(null);
                setMovieReviewRepliesCount(0);
            }
        };

        loadMovieReviewDiscussion();
    }, [id]);


    return (
        <React.Fragment>
            {/* Helmet pour le SEO */}
            <Helmet>
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "Movie",
                        "name": detail.title,
                        "genre": genres.map(genre => genre.name),
                        "director": directors.map((director) => ({
                            "@type": "Person",
                            "name": director.name,
                            "url": `https://moviefrenzy.ca/acteurDetails/${director.id}?role=director`,
                        })),
                        "datePublished": detail.release_date,
                        "actor": actors.map(actor => ({
                            "@type": "Person",
                            "name": actor.name
                        })),
                        "aggregateRating": {
                            "@type": "AggregateRating",
                            "ratingValue": detail.vote_average,
                            "reviewCount": detail.vote_count
                        }
                    })}
                </script>
            </Helmet>
            <NavigationBar />
            <ActivityGainToast
                visible={Boolean(activityGainToast)}
                toastId={activityGainToast?.id}
                points={activityGainToast?.points}
                unitLabel={activityGainToast?.unitLabel}
                label={activityGainToast?.label}
                variant={activityGainToast?.variant}
                onDone={() => setActivityGainToast(null)}
            />
            {isMovieReviewModalOpen && (
                <div className="movie-review-modal-overlay">
                    <div className="movie-review-modal">
                        <button
                            type="button"
                            className="movie-review-modal__close"
                            onClick={handleCloseMovieReview}
                            aria-label={t("common.close")}
                        >
                            ×
                        </button>

                        <h3 className="movie-review-modal__title">
                            {movieReviewModalTitle}
                        </h3>
                        <p className="movie-review-modal__rating">
                            {t("movie.rating.yourRating")} : <strong>{userMovieRating}/10</strong>
                        </p>

                        <label className="movie-review-modal__label" htmlFor="movie-review-draft">
                            {t("movie.rating.reviewQuestion")}
                        </label>

                        <textarea
                            id="movie-review-draft"
                            className="movie-review-modal__textarea"
                            value={movieReviewDraft}
                            onChange={(event) => setMovieReviewDraft(event.target.value)}
                            placeholder={movieReviewPlaceholder}
                            rows={6}
                        />

                        <div className="movie-review-modal__actions">
                            <button
                                type="button"
                                className="movie-review-modal__btn movie-review-modal__btn--secondary"
                                onClick={handleCloseMovieReview}
                            >
                                {t("movie.rating.cancelReview")}
                            </button>

                            <button
                                type="button"
                                className="movie-review-modal__btn movie-review-modal__btn--primary"
                                onClick={handleSubmitMovieReviewDraft}
                                disabled={movieReviewDraft.trim().length < 3}
                            >
                                {movieReviewSubmitLabel}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <ActeurContext.Provider value={{ handleActeurClick }}>
                <div className="ficheFilm" style={{
                    backgroundImage: `url(${darkwaterImageUrl})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                    backgroundAttachment: "fixed",
                    backgroundBlendMode: "overlay",
                    backgroundColor: "#200211",
                    opacity: "0.9",
                    overflowX: "hidden",
                    overflowY: "auto",
                }}>
                    <div className="video-wrapper">
                        <div className="video-background">
                            {shouldShowVideoCategorySelector && (
                                <div
                                    className="video-category-selector"
                                    role="group"
                                    aria-label={t("movie.videoCategories.selectorLabel")}
                                    style={{
                                        "--video-category-count":
                                            availableVideoCategories.length,

                                        "--video-category-index":
                                            availableVideoCategories.indexOf(
                                                activeVideoCategory
                                            ),
                                    }}
                                >
                                    {availableVideoCategories.map((category) => (
                                        <button
                                            type="button"
                                            className="video-category-selector__button"
                                            key={category}
                                            disabled={isKinoLoading}
                                            aria-pressed={
                                                activeVideoCategory === category
                                            }
                                            onClick={() =>
                                                handleVideoCategoryChange(category)
                                            }
                                        >
                                            {videoCategoryLabels[category]}
                                        </button>
                                    ))}
                                </div>
                            )}
                            {videoKey ? (
                                <div className="video-overlay">
                                    <div className="youtube-container">
                                        <YouTube
                                            key={id + ":" + currentLanguage + ":" + videoKey}
                                            videoId={videoKey}
                                            opts={videoOptions}
                                            onReady={_onReady}
                                            onError={_onError}
                                        />

                                        <div
                                            className="logoPinkTownYoutube"
                                            style={{ backgroundImage: `url(${logoPinkTownBgUrl})` }}
                                            aria-hidden="true"
                                        />

                                        {/* ZONE GRAPHIQUE CAST MOVIEFRENZY */}
                                        {/* Note : L'icône de Cast native étant restreinte par YouTube dans les iframes web mobiles, */}
                                        {/* ce bouton reste un élément esthétique haut de gamme pour la charte graphique. */}
                                        <div className="cast-preview-zone">
                                            <button
                                                type="button"
                                                className="moviefrenzy-cast-preview"
                                                disabled
                                                aria-label={t("movie.castUnavailableAria")}
                                            >
                                                <svg
                                                    className="moviefrenzy-cast-preview__icon"
                                                    viewBox="0 0 24 24"
                                                    aria-hidden="true"
                                                >
                                                    <path d="M1 18v3h3c0-1.66-1.34-3-3-3zm0-4v2c2.76 0 5 2.24 5 5h2c0-3.87-3.13-7-7-7zm0-4v2c4.97 0 9 4.03 9 9h2c0-6.08-4.92-11-11-11zm2-7h18v14h-5v2h5c1.1 0 2-.9 2-2V3c0-1.1-.9-2-2-2H3C1.9 1 1 1.9 1 3v5h2V3z" />
                                                </svg>

                                                <span>CAST</span>
                                            </button>
                                        </div>


                                    </div>
                                </div>
                            ) : (
                                <div className="no-video-background">
                                    <div className="poster-background" style={{ backgroundImage: `url('https://image.tmdb.org/t/p/w300${detail.poster_path}')`, zIndex: `10000` }}></div>
                                    <div className="no-video-text-container">
                                        <h2 className="no-video-text" role="status">
                                            {t(
                                                isKinoLoading
                                                    ? "movie.kinoLoading"
                                                    : "movie.noTrailerAvailable"
                                            )}
                                        </h2>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="container-fluid">
                        <div className="row align-items-start">
                            <div className="col-md-3 d-flex flex-column align-items-start">
                                <div id="dateTime">
                                    <p>
                                        {t('movie.releaseYear')}: <span id="spanInfoMovie">{detail.release_date?.substring(0, 4)}</span>
                                        <span> | </span> {t("movie.duration")}:{" "}
                                        <span id="spanInfoMovie">
                                            {Number.isFinite(detail.runtime) && detail.runtime > 0
                                                ? `${detail.runtime} ${t("movie.minutes")}`
                                                : t("movie.durationUnknown")}
                                        </span>
                                        {displayedProductionCountries.length > 0 && (
                                            <>
                                                <span className="production-countries-label">
                                                    <span className="production-countries-separator"> | </span>
                                                    {t("movie.productionCountries")}:
                                                </span>

                                                <span className="production-countries">
                                                    {displayedProductionCountries.map((country) => {
                                                        const countryName = country.isQuebec
                                                            ? country.name
                                                            : (
                                                                countryNamesFormatter?.of(country.iso_3166_1) ||
                                                                country.name ||
                                                                country.iso_3166_1
                                                            );

                                                        const tooltipId = `production-country-${country.iso_3166_1}`;

                                                        return (
                                                            <span
                                                                className="production-country-flag"
                                                                key={country.iso_3166_1}
                                                                tabIndex={0}
                                                                aria-describedby={tooltipId}
                                                                onPointerUp={(event) => {
                                                                    if (event.pointerType === "touch") {
                                                                        const flagElement = event.currentTarget;

                                                                        flagElement.focus();

                                                                        window.setTimeout(() => {
                                                                            flagElement.blur();
                                                                        }, 1800);
                                                                    }
                                                                }}
                                                            >
                                                                {country.isQuebec ? (
                                                                    <img
                                                                        src={`${process.env.PUBLIC_URL}/images/quebecFlag.svg`}
                                                                        className="flag-icon production-country-flag__quebec-image"
                                                                        alt=""
                                                                        aria-hidden="true"
                                                                    />
                                                                ) : (
                                                                    <Flag
                                                                        code={country.iso_3166_1}
                                                                        className="flag-icon"
                                                                        alt=""
                                                                        aria-hidden="true"
                                                                    />
                                                                )}

                                                                <span
                                                                    id={tooltipId}
                                                                    className="production-country-tooltip"
                                                                    role="tooltip"
                                                                >
                                                                    {countryName}
                                                                </span>
                                                            </span>
                                                        );
                                                    })}
                                                </span>

                                            </>
                                        )}
                                    </p>
                                    {isLoggedIn && (
                                        <div
                                            className="favoriteMovie"
                                            onClick={addFavoritePreview}
                                            onKeyDown={(event) => {
                                                if (
                                                    event.key === "Enter" ||
                                                    event.key === " "
                                                ) {
                                                    event.preventDefault();
                                                    addFavoritePreview();
                                                }
                                            }}
                                            role="button"
                                            tabIndex={0}
                                            aria-label={t("movie.addFavoriteAria")}
                                        >
                                            <img
                                                src={`${process.env.PUBLIC_URL}/images/favoriteMovie01.png`}
                                                alt=""
                                                aria-hidden="true"
                                                className="coeurMovie"
                                            />
                                        </div>
                                    )}
                                    {/* 2. Afficher le message selon l'état */}
                                    {showConfirmation && <h2 style={{ color: "#00FFFF", margin: "8vh 0 0 5vw" }}>{t('movie.addedToFavorites')}</h2>}
                                    {showLimitMessage && <h2 style={{ color: "#FF0000", margin: "8vh 0 0 5vw" }}>{t('movie.favoritesLimitReached')}</h2>}
                                    {showAlreadyInFavorites && <h2 style={{ color: "#FFA500", margin: "8vh 0 0 5vw" }}>{t('movie.alreadyInFavorites')}</h2>}
                                </div>
                            </div>
                            <div className="col-md-6 text-center">
                                <h1 className="movie-titleFiche">{detail.title}</h1>
                                <div className="movie-genresStyle">
                                    {genres.map((genre, index) => (
                                        <Link
                                            key={genre.id}
                                            to={{
                                                pathname: `/categories/${genre.id}`,
                                                state: {
                                                    id: genre.id,
                                                    name: genre.name,
                                                },
                                            }}
                                            className="text-genre"
                                            style={{ marginRight: "10px" }}
                                        >
                                            {genre.name}
                                        </Link>
                                    ))}
                                </div>
                            </div>
                            <div className="col-md-3 d-flex flex-column align-items-start">
                                <div className={`sectChart ${isMovieFrenzyRatingMode ? "sectChart--moviefrenzy" : "sectChart--tmdb"}`}>
                                    <div className="rating-source-toggle" role="group" aria-label={t("movie.rating.sourceToggle")}>
                                        <button
                                            type="button"
                                            className={`rating-source-toggle__btn ${ratingMode === "tmdb" ? "active" : ""
                                                }`}
                                            onClick={() => handleRatingModeChange("tmdb")}
                                            aria-pressed={ratingMode === "tmdb"}
                                        >
                                            <img
                                                src={`${process.env.PUBLIC_URL}/images/tmdb.png`}
                                                alt=""
                                                aria-hidden="true"
                                                className="rating-source-toggle__logo"
                                            />
                                            <span>TMDB</span>
                                        </button>

                                        <button
                                            type="button"
                                            className={`rating-source-toggle__btn ${ratingMode === "moviefrenzy" ? "active" : ""
                                                }`}
                                            onClick={() => handleRatingModeChange("moviefrenzy")}
                                            aria-pressed={ratingMode === "moviefrenzy"}
                                        >
                                            <img
                                                src={`${process.env.PUBLIC_URL}/images/newColorLogoMovieFrenzy_01.png`}
                                                alt=""
                                                aria-hidden="true"
                                                className="rating-source-toggle__logo"
                                            />
                                            <span>MovieFrenzy</span>
                                        </button>
                                    </div>

                                    <h2 className="text-uppercase mb-3 noteCinephile">{ratingTitle}</h2>

                                    <div className={`chart-container chart-wrapper ${isMovieFrenzyRatingMode ? "chart-wrapper--moviefrenzy" : ""}`}>
                                        <Doughnut
                                            key={`${ratingMode}-${isVisible ? "visible-chart" : "hidden-chart"}`}
                                            data={chartData}
                                            options={chartOptions}
                                        />

                                        <div
                                            className={`chart-text ${activeRatingPercent === null ? "chart-text--pending" : ""}`}
                                            style={chartTextStyle}
                                        >
                                            {activeRatingPercent === null
                                                ? t("movie.rating.noRatingsYet")
                                                : `${animatedPercent}%`}
                                        </div>
                                    </div>

                                    <p className="rating-source-caption">{ratingCaption}</p>
                                    <OmdbInsightsPopover
                                        data={omdbData}
                                        movieTitle={detail.title}
                                    />
                                    {ratingMode === "moviefrenzy" && movieReviewForumPostId && (
                                        <button
                                            type="button"
                                            className="movie-review-entry__link"
                                            onClick={() => history(`/subject/${movieReviewForumPostId}`)}
                                        >
                                            {movieReviewLinkLabel}
                                        </button>
                                    )}

                                    {ratingMode === "moviefrenzy" && (
                                        <>
                                            <div className="user-rating-preview">
                                                {isLoggedIn ? (
                                                    isUserRatingLoading ? (
                                                        <p>{t("movie.rating.loadingUserRating")}</p>
                                                    ) : userMovieRating !== null ? (
                                                        <p>
                                                            <span className="user-rating-preview__label">{t("movie.rating.yourRating")}</span>
                                                            <strong>{userMovieRating}/10</strong>
                                                        </p>
                                                    ) : (
                                                        <p>{t("movie.rating.notRatedYet")}</p>
                                                    )
                                                ) : (
                                                    <p>{t("movie.rating.loginToRate")}</p>
                                                )}
                                            </div>
                                            {canShareMovieFrenzyReview && (
                                                <div className="movie-review-entry">
                                                    {hasExistingMovieReview ? (
                                                        <div className="movie-review-entry__actions">
                                                            <button
                                                                type="button"
                                                                className="user-rating-save-btn movie-review-entry__button movie-review-entry__button--secondary"
                                                                onClick={handleViewMovieReview}
                                                                disabled={isDeletingMovieReview}
                                                            >
                                                                {t("movie.rating.viewMyReview")}
                                                            </button>

                                                            <button
                                                                type="button"
                                                                className="user-rating-save-btn movie-review-entry__button"
                                                                onClick={handleOpenMovieReview}
                                                                disabled={isDeletingMovieReview}
                                                            >
                                                                {t("movie.rating.editReview")}
                                                            </button>

                                                            <button
                                                                type="button"
                                                                className="user-rating-save-btn movie-review-entry__button movie-review-entry__button--danger"
                                                                onClick={handleDeleteMovieReview}
                                                                disabled={isDeletingMovieReview}
                                                            >
                                                                {isDeletingMovieReview ? t("movie.rating.deletingReview") : t("movie.rating.deleteReview")}
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <button
                                                            type="button"
                                                            className="user-rating-save-btn movie-review-entry__button"
                                                            onClick={handleOpenMovieReview}
                                                        >
                                                            {t("movie.rating.shareReview")}
                                                        </button>
                                                    )}
                                                    <p className="movie-review-entry__help">
                                                        {t("movie.rating.reviewHelp")}
                                                    </p>
                                                </div>
                                            )}

                                            {isLoggedIn && !isUserRatingLoading && (
                                                <div className="user-rating-selector" aria-label={t("movie.rating.ratingSelectorAria")}>
                                                    <p className="user-rating-selector__title">{t("movie.rating.chooseYourRating")}</p>

                                                    <div className="user-rating-selector__buttons">
                                                        {Array.from({ length: 10 }, (_, index) => {
                                                            const ratingValue = index + 1;

                                                            return (
                                                                <button
                                                                    key={ratingValue}
                                                                    type="button"
                                                                    className={`user-rating-selector__btn ${selectedUserMovieRating === ratingValue ? "active" : ""}`}
                                                                    onClick={() => {
                                                                        setSelectedUserMovieRating(ratingValue);
                                                                        setUserRatingMessage(
                                                                            t("movie.rating.preparedRating").replace("{{rating}}", ratingValue)
                                                                        );
                                                                    }}
                                                                    aria-pressed={selectedUserMovieRating === ratingValue}
                                                                >
                                                                    {ratingValue}
                                                                </button>
                                                            );
                                                        })}
                                                    </div>

                                                    <button
                                                        type="button"
                                                        className="user-rating-save-btn"
                                                        onClick={handleSaveUserMovieRating}
                                                        disabled={isSavingUserRating}
                                                    >
                                                        {isSavingUserRating ? t("movie.rating.savingRating") : t("movie.rating.saveRating")}
                                                    </button>
                                                    {userRatingMessage && (
                                                        <p className="user-rating-message">{userRatingMessage}</p>
                                                    )}
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div ref={overviewSectionRef} className="row movie-overview-container">
                            <div className="col-12 col-md-4 movie-overview mobile-movie-overview">
                                <p>
                                    {String(detail.overview || "").trim() ||
                                        t("movie.synopsisUnavailableText")}
                                </p>
                            </div>
                            <div className="col-md-4 d-flex afficheInfo">
                                <div className="col-lg-6">
                                    <img
                                        src={`https://image.tmdb.org/t/p/w300${detail.poster_path}`}
                                        alt={detail.title}
                                        className="img-fluid imgFluidAffiche"
                                    />
                                </div>
                                <div className="col-lg-6">
                                    <div className="general-info-section" id="infoGeneral">
                                        <div className="general-info-circle ficheFilmGeneral-Whimsy">
                                            <span
                                                className="ficheFilmGeneral-whimsy-particle ficheFilmGeneral-whimsy-particle--cyan"
                                                aria-hidden="true"
                                            />

                                            <span
                                                className="ficheFilmGeneral-whimsy-particle ficheFilmGeneral-whimsy-particle--magenta"
                                                aria-hidden="true"
                                            />

                                            <span
                                                className="ficheFilmGeneral-whimsy-particle ficheFilmGeneral-whimsy-particle--pink"
                                                aria-hidden="true"
                                            />
                                            <div className="inner-circle"></div>
                                        </div>
                                        <p className="info-text">{t('movie.generalInfo')} <span className="info-highlight">{detail.title}</span></p>
                                        <p className="info-text">
                                            {t("movie.director")} :{" "}

                                            <span className="info-highlight">
                                                {directors.length > 0
                                                    ? directors.map((director, index) => (
                                                        <React.Fragment key={director.id}>
                                                            {index > 0 && ", "}

                                                            <Link
                                                                to={`/acteurDetails/${director.id}?role=director`}
                                                                className="person-credit-link"
                                                            >
                                                                {director.name}
                                                            </Link>
                                                        </React.Fragment>
                                                    ))
                                                    : "—"}
                                            </span>
                                        </p>
                                        <p className="info-text">{t('movie.originalTitle')} : <span className="info-highlight">{detail.original_title}</span></p>
                                        <p className="info-text">{t('movie.screenwriter')} : <span className="info-highlight">{detail.writer}</span></p>
                                        <div className="info-text maturity-rating">
                                            <span className="maturity-rating__heading">
                                                {t("movie.ageRating")} :
                                            </span>

                                            <span
                                                className={`
                                                    mf-maturity-badge
                                                    mf-maturity-badge--${maturityBadge.level}
                                                `}
                                                aria-hidden="true"
                                            >
                                                <span className="mf-maturity-badge__system">
                                                    {isOfficialQuebecRating && (
                                                        <img
                                                            src={`${process.env.PUBLIC_URL}/images/quebecFlag.svg`}
                                                            className="mf-maturity-badge__quebec-flag"
                                                            alt=""
                                                        />
                                                    )}

                                                    MF · {maturityBadge.system}
                                                </span>

                                                <span className="mf-maturity-badge__code">
                                                    {maturityBadge.code}
                                                </span>
                                            </span>

                                            <span className="info-highlight maturity-rating__label">
                                                {maturityLabel}.
                                            </span>

                                            {isOfficialQuebecRating && (
                                                <div className="quebec-rating-details">
                                                    <div className="quebec-rating-details__header">
                                                        <img
                                                            src={`${process.env.PUBLIC_URL}/images/quebecFlag.svg`}
                                                            className="quebec-rating-details__flag"
                                                            alt=""
                                                            aria-hidden="true"
                                                        />

                                                        {maturityRating?.sourceUrl ? (
                                                            <a
                                                                href={maturityRating.sourceUrl}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                className="quebec-rating-details__source"
                                                            >
                                                                {t("movie.rating.officialQuebecRating")}
                                                            </a>
                                                        ) : (
                                                            <span className="quebec-rating-details__source">
                                                                {t("movie.rating.officialQuebecRating")}
                                                            </span>
                                                        )}
                                                    </div>

                                                    {quebecAdvisoryLabels.length > 0 && (
                                                        <ul className="quebec-rating-advisories">
                                                            {quebecAdvisoryLabels.map((advisory) => (
                                                                <li
                                                                    key={advisory}
                                                                    className="quebec-rating-advisories__item"
                                                                >
                                                                    {advisory}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-12">
                                <Container className="mt-5 actors-container">
                                    <div className="movie-actors">
                                        <h5>{t('movie.actors')}:</h5>
                                        <div className="actors-grid">
                                            {actors && actors.slice(0, 4).map((actor, index) => (
                                                <div
                                                    key={actor.id || index}
                                                    className="actor-card"
                                                >
                                                    <Link
                                                        to={{
                                                            pathname: `/acteurDetails/${actor.id}`,
                                                            state: { id: actor.id },
                                                        }}
                                                        className="linkActorFicheFilm"
                                                    >
                                                        <img
                                                            src={
                                                                actor.profile_path
                                                                    ? `https://image.tmdb.org/t/p/w500${actor.profile_path}`
                                                                    : "https://zupimages.net/up/23/14/zdoe.jpg"
                                                            }
                                                            alt=""
                                                            aria-hidden="true"
                                                            className={`substitute-image${index === 3 ? " mainActors-image4" : ""
                                                                }`}
                                                        />

                                                        <div className="actor-name-container">
                                                            <li
                                                                className="actor-name"
                                                            >
                                                                {actor.original_name}
                                                            </li>

                                                            <li className="actor-name actor-character">
                                                                {actor.character}
                                                            </li>
                                                        </div>
                                                    </Link>
                                                </div>
                                            ))}
                                        </div>
                                        {actors && actors.length > 4 && (
                                            <div className="row">
                                                <div className="col-lg-6">
                                                    <div className="secondary-roles">
                                                        <h5>{t('movie.secondaryRoles')}:</h5>
                                                        <p>
                                                            {actors.slice(4).map((actor, index) => (
                                                                <span key={index}>{actor.original_name}{index < actors.slice(4).length - 1 && ", "}</span>
                                                            ))}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </Container>
                            </div>
                        </div>
                    </div>
                </div>
            </ActeurContext.Provider>
            <div className="actor-details-footer"
                style={{ maxWidth: '100%', maxHeight: '100%', position: 'relative' }}
            >
                <Footer />
            </div>
        </React.Fragment>

    );
};

export default FicheFilm;







// import React, { useState, useEffect, useContext, useCallback, useMemo, useRef } from "react";
// import NavigationBar from "../components/Navigation";
// import axios from "axios";
// import { Container, Button } from "reactstrap";
// import Actor from "../components/Actor";
// import { useParams, useNavigate } from "react-router-dom";
// import Footer from "../components/Footer";
// import Genre from "../components/Genre";
// import YouTube from "react-youtube";
// import { Link } from "react-router-dom";
// import { Doughnut, Pie } from "react-chartjs-2";
// import { Chart, ArcElement, LineElement, BarElement, PointElement, BarController, LineController, PieController, CategoryScale, LinearScale, RadialLinearScale, TimeScale } from 'chart.js';
// import ActeurContext from '../contexts/ActeurContext';
// import { UserContext } from '../UserContext';
// import Flag from 'react-world-flags';
// import { db, auth } from "../config/firebasedeconfig";
// import { doc, getDoc, updateDoc, runTransaction, serverTimestamp, collection, addDoc, query, where, getDocs, limit, increment, deleteField, deleteDoc, collectionGroup, writeBatch } from "firebase/firestore";
// import { Helmet } from "react-helmet-async"; // Ajout de Helmet pour le SEO
// import { useLanguage } from '../contexts/LanguageContext';
// import ActivityGainToast from "../components/ActivityGainToast";
// import cld from '../config/cloudinaryConfig';
// import { format, quality } from '@cloudinary/url-gen/actions/delivery';
// import { findQuebecRating } from "../utils/quebecRatingUtils";
// import {
//     getMovieVideoFallbacks
// } from "../utils/movieVideoFallbacks";
// import {
//     findQuebecProduction
// } from "../utils/quebecProductionUtils";
// import {
//     fetchOmdbMovieData
// } from "../utils/omdbUtils";
// import OmdbInsightsPopover from "../components/OmdbInsightsPopover";
// import {
//     buildMaturityRating,
//     formatCertificationLabel,
//     getMaturityBadgePresentation,
//     getQuebecAdvisoryLabels,
//     normalizeCertificationCode,
//     resolveRatingSystem,
// } from "../utils/movieMaturityUtils";
// import { fetchKinoCheckTrailers } from "../utils/kinocheckUtils";


// Chart.register(
//     ArcElement,
//     LineElement,
//     BarElement,
//     PointElement,
//     BarController,
//     LineController,
//     PieController,
//     CategoryScale,
//     LinearScale,
//     RadialLinearScale,
//     TimeScale
// );

// const TMDB_API_KEY = process.env.REACT_APP_TMDB_API_KEY;

// const darkwaterImageUrl = cld
//     .image('darkwater_jkk0bl')
//     .delivery(quality('auto'))
//     .delivery(format('auto'))
//     .toURL();

// const logoPinkTownBgUrl = cld
//     .image('logoFooterChatGPT26_oxnohb')
//     .delivery(quality('auto'))
//     .delivery(format('auto'))
//     .toURL();

// const TMDB_LOCALES = {
//     fr: "fr-FR",
//     en: "en-US",
//     es: "es-ES",
//     de: "de-DE",
// };

// const getTmdbLocale = (language) => {
//     return TMDB_LOCALES[language] || language || "en-US";
// };

// const normalizeVideoTitle = (title = "") => {
//     return String(title)
//         .normalize("NFD")
//         .replace(/[\u0300-\u036f]/g, "")
//         .toLowerCase();
// };

// const isSecondaryPromotionalVideo = (video) => {
//     const normalizedTitle = normalizeVideoTitle(video?.name);

//     const secondaryPatterns = [
//         /\bteaser\b/,
//         /\b(?:tv\s*)?spot\b/,
//         /\bcountdown\b/,
//         /\bin cinemas now\b/,
//         /\bnow playing\b/,
//         /\bj\s*-\s*\d+\b/,

//         // Promotions particulières ou centrées sur un aspect du film.
//         /\bspecial\s+(?:trailer|pv)\b/,
//         /\bopening\s+(?:theme|song)\b/,
//         /\bintroducing\b/,
//         /\bcharacter\s+(?:trailer|promo|video)\b/,

//         // Variantes dans certaines langues.
//         /\bbande[-\s]?annonce\s+speciale?\b/,
//         /\btrailer\s+especial\b/,
//         /\bspezialtrailer\b/,

//         // Variantes japonaises fréquentes.
//         /スペシャル\s*(?:pv|trailer)/,
//         /オープニング(?:テーマ|主題歌)?/,
//         /キャラクター\s*(?:pv|trailer)?/,
//     ];

//     return secondaryPatterns.some((pattern) =>
//         pattern.test(normalizedTitle)
//     );
// };

// const getTrailerScore = (video, preferredLanguage) => {
//     const normalizedTitle = normalizeVideoTitle(video.name);
//     let score = 0;

//     // Priorité très forte aux vidéos reconnues comme officielles par TMDB.
//     if (video.official === true) {
//         score += 1000;
//     }

//     // Priorité à la langue actuellement sélectionnée.
//     if (video.iso_639_1 === preferredLanguage) {
//         score += 150;
//     }

//     // Bonus lorsque le titre lui-même mentionne son caractère officiel.
//     if (
//         /\bofficial\b/.test(normalizedTitle) ||
//         /\bofficiel(?:le)?\b/.test(normalizedTitle) ||
//         /\boficial\b/.test(normalizedTitle) ||
//         /\boffiziell(?:e|er|es|en)?\b/.test(normalizedTitle)
//     ) {
//         score += 100;
//     }

//     // En français, une VF passe avant une VOST.
//     if (preferredLanguage === "fr") {
//         if (/\bvf\b/.test(normalizedTitle)) {
//             score += 60;
//         }

//         if (/\bvost(?:fr)?\b/.test(normalizedTitle)) {
//             score += 20;
//         }
//     }

//     // Les trailers finaux et numérotés restent acceptables,
//     // mais passent après une bande-annonce principale équivalente.
//     if (/\bfinal(?:e|er|es)?\b/.test(normalizedTitle)) {
//         score -= 25;
//     }

//     if (
//         /(?:#|n[°o]?\s*)\d+\b|\btrailer\s*\d+\b/.test(normalizedTitle)
//     ) {
//         score -= 15;
//     }

//     // Petit avantage à la meilleure résolution renseignée par TMDB.
//     if (Number.isFinite(Number(video.size))) {
//         score += Math.min(Number(video.size), 2160) / 100;
//     }

//     return score;
// };

// const isUsableYouTubeVideo = (video) => {
//     return (
//         video?.site?.toLowerCase() === "youtube" &&
//         Boolean(video.key)
//     );
// };

// const getNormalizedVideoType = (video) => {
//     return String(video?.type || "")
//         .trim()
//         .toLowerCase();
// };

// const sortTrailerCandidates = (
//     videos = [],
//     preferredLanguage
// ) => {
//     return [...videos].sort((firstVideo, secondVideo) => {
//         const scoreDifference =
//             getTrailerScore(secondVideo, preferredLanguage) -
//             getTrailerScore(firstVideo, preferredLanguage);

//         if (scoreDifference !== 0) {
//             return scoreDifference;
//         }

//         // En cas d’égalité, prendre la publication la plus récente.
//         return (
//             new Date(secondVideo.published_at || 0).getTime() -
//             new Date(firstVideo.published_at || 0).getTime()
//         );
//     });
// };

// const rankVideoCandidates = (
//     videos = [],
//     preferredLanguage
// ) => {
//     const usableVideos = videos.filter(
//         isUsableYouTubeVideo
//     );

//     const usableTrailers = usableVideos.filter(
//         (video) =>
//             getNormalizedVideoType(video) === "trailer"
//     );

//     const mainTrailerCandidates = usableTrailers.filter(
//         (video) => !isSecondaryPromotionalVideo(video)
//     );

//     const backupTrailerCandidates = usableTrailers.filter(
//         isSecondaryPromotionalVideo
//     );

//     const behindScenesCandidates = usableVideos.filter(
//         (video) => {
//             const videoType = getNormalizedVideoType(video);

//             return (
//                 videoType === "behind the scenes" ||
//                 videoType === "featurette"
//             );
//         }
//     );

//     const bonusCandidates = usableVideos.filter(
//         (video) => {
//             const videoType = getNormalizedVideoType(video);

//             return (
//                 videoType === "clip" ||
//                 videoType === "teaser" ||
//                 videoType === "bloopers"
//             );
//         }
//     );

//     return {
//         main: sortTrailerCandidates(
//             mainTrailerCandidates,
//             preferredLanguage
//         ),

//         backup: sortTrailerCandidates(
//             backupTrailerCandidates,
//             preferredLanguage
//         ),

//         behindScenes: sortTrailerCandidates(
//             behindScenesCandidates,
//             preferredLanguage
//         ),

//         bonus: sortTrailerCandidates(
//             bonusCandidates,
//             preferredLanguage
//         ),
//     };
// };

// const isValidRatingMode = (mode) => {
//     return mode === "tmdb" || mode === "moviefrenzy";
// };

// const escapeHtml = (value = "") => {
//     return String(value)
//         .replaceAll("&", "&amp;")
//         .replaceAll("<", "&lt;")
//         .replaceAll(">", "&gt;")
//         .replaceAll('"', "&quot;")
//         .replaceAll("'", "&#039;");
// };

// const formatMovieReviewContent = ({ movieTitle, rating, review }) => {
//     const safeTitle = escapeHtml(movieTitle);
//     const safeReview = escapeHtml(review).replace(/\n/g, "<br />");

//     return `
//         <p><strong>Note MovieFrenzy : ${rating}/10</strong></p>
//         <p><strong>Film :</strong> ${safeTitle}</p>
//         <p>${safeReview}</p>
//     `;
// };

// const extractReviewTextFromForumContent = (htmlContent = "") => {
//     const normalizedHtml = String(htmlContent).replace(/<br\s*\/?>/gi, "\n");

//     const parser = new DOMParser();
//     const parsedDocument = parser.parseFromString(normalizedHtml, "text/html");

//     const paragraphs = Array.from(parsedDocument.querySelectorAll("p"));

//     if (paragraphs.length >= 3) {
//         return paragraphs
//             .slice(2)
//             .map((paragraph) => paragraph.textContent.trim())
//             .filter(Boolean)
//             .join("\n\n");
//     }

//     return parsedDocument.body.textContent?.trim() || "";
// };

// const createMovieReviewExcerpt = (reviewText = "", maxLength = 180) => {
//     const normalizedText = String(reviewText || "")
//         .replace(/\s+/g, " ")
//         .trim();

//     if (normalizedText.length <= maxLength) {
//         return normalizedText;
//     }

//     return `${normalizedText.slice(0, maxLength - 1).trim()}…`;
// };

// /*
//  * Le registre québécois emploie certains codes
//  * nationaux différents des codes ISO utilisés
//  * par TMDB et par le composant Flag.
//  */
// const REGISTRY_NATION_TO_ISO2 = new Map([
//     ["SUE", "SE"],
// ]);

// const FicheFilm = (props) => {
//     const { t, currentLanguage } = useLanguage();
//     const countryNamesFormatter = useMemo(() => {
//         try {
//             return new Intl.DisplayNames(
//                 [currentLanguage],
//                 { type: "region" }
//             );
//         } catch (error) {
//             return null;
//         }
//     }, [currentLanguage]);
//     const { id } = useParams();
//     const [movie, setMovie] = useState({});
//     const [detail, setDetail] = useState({
//         writer: "",
//     });

//     const [omdbData, setOmdbData] =
//         useState(null);

//     const [maturityRating, setMaturityRating] =
//         useState(null);
//     const [
//         quebecProductionInfo,
//         setQuebecProductionInfo
//     ] = useState(null);
//     const [genres, setGenres] = useState([]);
//     const [actors, setActors] = useState([]);
//     const [directors, setDirectors] = useState([]);
//     const [credits, setCredits] = useState([]);
//     const history = useNavigate();
//     const [videoKey, setVideoKey] = useState("");

//     const [activeVideoCategory, setActiveVideoCategory] =
//         useState("trailer");

//     const [
//         videoCandidatesByCategory,
//         setVideoCandidatesByCategory
//     ] = useState({
//         trailer: [],
//         behindScenes: [],
//         bonus: [],
//     });

//     const videoCandidates =
//         videoCandidatesByCategory[activeVideoCategory] || [];
//     const videoCategoryOrder = [
//         "trailer",
//         "behindScenes",
//         "bonus",
//     ];

//     const videoCategoryLabels = {
//         trailer: t("movie.videoCategories.trailer"),
//         behindScenes: t("movie.videoCategories.behindScenes"),
//         bonus: t("movie.videoCategories.bonus"),
//     };

//     const availableVideoCategories =
//         videoCategoryOrder.filter(
//             (category) =>
//                 videoCandidatesByCategory[category].length > 0
//         );

//     // Le sélecteur est inutile lorsqu’une bande-annonce
//     // est la seule catégorie disponible.
//     // Une catégorie unique différente de Trailer reste affichée
//     // afin d’indiquer clairement le type de vidéo présenté.
//     const shouldShowVideoCategorySelector =
//         availableVideoCategories.length > 1 ||
//         (
//             availableVideoCategories.length === 1 &&
//             availableVideoCategories[0] !== "trailer"
//         );
//     const rejectedVideoKeysRef = useRef(new Set());
//     const videoLoadRef = useRef(null);
//     const [isKinoLoading, setIsKinoLoading] = useState(false);
//     const overviewSectionRef = useRef(null);
//     const tmdbRatingsCount =
//         Number.isInteger(detail.vote_count) && detail.vote_count > 0
//             ? detail.vote_count
//             : 0;

//     const tmdbPercent =
//         tmdbRatingsCount > 0 && Number.isFinite(detail.vote_average)
//             ? (detail.vote_average * 10).toFixed(2)
//             : null;
//     const [movieFrenzyAverageRating, setMovieFrenzyAverageRating] = useState(null);
//     const [movieFrenzyRatingsCount, setMovieFrenzyRatingsCount] = useState(0);
//     const [userMovieRating, setUserMovieRating] = useState(null);
//     const [selectedUserMovieRating, setSelectedUserMovieRating] = useState(8);
//     const [userRatingMessage, setUserRatingMessage] = useState("");
//     const [isSavingUserRating, setIsSavingUserRating] = useState(false);
//     const [isMovieReviewModalOpen, setIsMovieReviewModalOpen] = useState(false);
//     const [movieReviewDraft, setMovieReviewDraft] = useState("");
//     const [isUserRatingLoading, setIsUserRatingLoading] = useState(false);
//     const movieFrenzyPercent = movieFrenzyAverageRating === null
//         ? null
//         : (Number(movieFrenzyAverageRating) * 10).toFixed(2);
//     const [showConfirmation, setShowConfirmation] = useState(false);
//     const [showLimitMessage, setShowLimitMessage] = useState(false);
//     const [showAlreadyInFavorites, setShowAlreadyInFavorites] = useState(false);
//     const [animatedPercent, setAnimatedPercent] = useState("0.00");
//     const [isVisible, setIsVisible] = useState(false);
//     const {
//         isLoggedIn,
//         userId,
//         username,
//         userData,
//     } = useContext(UserContext);
//     const ratingSystem = useMemo(() => {
//         return resolveRatingSystem({
//             country: userData?.country,
//             state: userData?.state,
//             language: currentLanguage,
//         });
//     }, [
//         userData?.country,
//         userData?.state,
//         currentLanguage,
//     ]);

//     const maturityBadge = useMemo(() => {
//         return getMaturityBadgePresentation({
//             maturityRating,
//             requestedSystem: ratingSystem,
//         });
//     }, [
//         maturityRating,
//         ratingSystem,
//     ]);

//     const maturityLabel =
//         maturityRating?.label ||
//         t("movie.rating.maturity.unrated");

//     const isOfficialQuebecRating =
//         maturityRating?.source ===
//         "quebec-official";

//     const isQuebecProduction =
//         quebecProductionInfo
//             ?.isQuebecProduction === true;

//     const displayedProductionCountries =
//         useMemo(() => {
//             const countries = [];
//             const seenCodes = new Set();

//             const addCountry = (country) => {
//                 const isoCode = String(
//                     country?.iso_3166_1 ?? ""
//                 )
//                     .trim()
//                     .toUpperCase();

//                 if (
//                     !isoCode ||
//                     seenCodes.has(isoCode)
//                 ) {
//                     return;
//                 }

//                 seenCodes.add(isoCode);

//                 countries.push({
//                     ...country,
//                     iso_3166_1: isoCode,
//                     isQuebec: false,
//                 });
//             };

//             let quebecInserted = false;

//             const addQuebec = () => {
//                 if (
//                     !isQuebecProduction ||
//                     quebecInserted
//                 ) {
//                     return;
//                 }

//                 countries.push({
//                     iso_3166_1: "QC",
//                     name: "Québec",
//                     isQuebec: true,
//                 });

//                 seenCodes.add("QC");
//                 quebecInserted = true;
//             };

//             for (
//                 const country of
//                 detail.production_countries ?? []
//             ) {
//                 addCountry(country);

//                 /*
//                  * Le Québec est placé immédiatement
//                  * après le Canada lorsqu’il est présent.
//                  */
//                 if (
//                     String(
//                         country?.iso_3166_1
//                     ).toUpperCase() === "CA"
//                 ) {
//                     addQuebec();
//                 }
//             }

//             /*
//              * Permet d’afficher Québec même lorsque
//              * TMDB omet complètement le Canada.
//              */
//             addQuebec();

//             for (
//                 const nation of
//                 quebecProductionInfo?.nations ?? []
//             ) {
//                 const registryCode =
//                     String(nation)
//                         .trim()
//                         .toUpperCase();

//                 const isoCode =
//                     REGISTRY_NATION_TO_ISO2.get(
//                         registryCode
//                     );

//                 if (isoCode) {
//                     addCountry({
//                         iso_3166_1: isoCode,
//                     });
//                 }
//             }

//             return countries;
//         }, [
//             detail.production_countries,
//             isQuebecProduction,
//             quebecProductionInfo?.nations,
//         ]);

//     const quebecAdvisoryLabels =
//         getQuebecAdvisoryLabels(
//             maturityRating?.advisories,
//             t
//         );

//     const [movieReviewForumPostId, setMovieReviewForumPostId] = useState(null);
//     const [movieReviewRepliesCount, setMovieReviewRepliesCount] = useState(0);
//     const [ratingMode, setRatingMode] = useState("tmdb");
//     const [movieReviewMeta, setMovieReviewMeta] = useState(null);
//     const [isDeletingMovieReview, setIsDeletingMovieReview] = useState(false);
//     const [activityGainToast, setActivityGainToast] = useState(null);
//     const [chartData, setChartData] = useState({
//         datasets: [
//             {
//                 data: [0, 100],
//                 backgroundColor: ["#FF33CC", "#333333"],
//                 borderWidth: 0,
//             },
//         ],
//     });
//     const [chartTextStyle, setChartTextStyle] = useState({});

//     const isMovieFrenzyRatingMode = ratingMode === "moviefrenzy";

//     const canShareMovieFrenzyReview =
//         isLoggedIn &&
//         userId &&
//         isMovieFrenzyRatingMode &&
//         !isUserRatingLoading &&
//         userMovieRating !== null;

//     const hasExistingMovieReview =
//         movieReviewMeta?.hasReview === true &&
//         Boolean(movieReviewMeta?.forumPostId) &&
//         Boolean(movieReviewMeta?.forumReplyId);

//     // const movieReviewActionLabel = hasExistingMovieReview
//     //     ? t("movie.rating.editReview")
//     //     : t("movie.rating.shareReview");

//     const movieReviewSubmitLabel = hasExistingMovieReview
//         ? t("movie.rating.updateReview")
//         : t("movie.rating.publishReview");

//     const movieReviewPlaceholder = hasExistingMovieReview
//         ? t("movie.rating.editReviewPlaceholder")
//         : t("movie.rating.reviewPlaceholder");

//     const movieReviewModalTitle = hasExistingMovieReview
//         ? t("movie.rating.editReviewModalTitle")
//         : t("movie.rating.reviewModalTitle");

//     const safeMovieReviewRepliesCount = Number(movieReviewRepliesCount) || 0;

//     const movieReviewLinkLabel =
//         safeMovieReviewRepliesCount > 0
//             ? safeMovieReviewRepliesCount === 1
//                 ? t("movie.rating.readMovieFrenzyReviews_one").replace("{{count}}", safeMovieReviewRepliesCount)
//                 : t("movie.rating.readMovieFrenzyReviews_other").replace("{{count}}", safeMovieReviewRepliesCount)
//             : t("movie.rating.readMovieFrenzyReviews");

//     const activeRatingPercent = ratingMode === "tmdb"
//         ? tmdbPercent
//         : movieFrenzyPercent;

//     const activeChartPercent = activeRatingPercent === null
//         ? 0
//         : Number(activeRatingPercent);

//     const activeChartColors = useMemo(() => (
//         isMovieFrenzyRatingMode
//             ? ["#00FFFF", "#2B0013"]
//             : ["#FF33CC", "#333333"]
//     ), [isMovieFrenzyRatingMode]);

//     const activeChartHoverColors = useMemo(() => (
//         isMovieFrenzyRatingMode
//             ? ["#7CFFFF", "#200211"]
//             : ["#FF80DF", "#2b2b2b"]
//     ), [isMovieFrenzyRatingMode]);

//     const ratingTitle = ratingMode === "tmdb"
//         ? t("movie.rating.scoreTmdb")
//         : t("movie.rating.scoreMovieFrenzy");

//     const activeRatingsCount = isMovieFrenzyRatingMode
//         ? movieFrenzyRatingsCount
//         : tmdbRatingsCount;

//     const activeRatingSource = isMovieFrenzyRatingMode
//         ? "MovieFrenzy"
//         : "TMDB";

//     const ratingCaption = t(
//         activeRatingsCount === 0
//             ? "movie.rating.noRatingsForSource"
//             : activeRatingsCount === 1
//                 ? "movie.rating.basedOnRatings_one"
//                 : "movie.rating.basedOnRatings_other"
//     )
//         .replace("{{count}}", activeRatingsCount.toLocaleString(currentLanguage))
//         .replace("{{source}}", activeRatingSource);


//     const handleRatingModeChange = async (nextMode) => {
//         if (!isValidRatingMode(nextMode)) return;

//         setRatingMode(nextMode);

//         if (!isLoggedIn || !userId) return;

//         try {
//             const userRef = doc(db, "users", userId);

//             await updateDoc(userRef, {
//                 "preferences.ratingSource": nextMode,
//             });
//         } catch (error) {
//             console.error("Erreur lors de la sauvegarde du mode de notation:", error);
//         }
//     };

//     useEffect(() => {
//         const loadRatingPreference = async () => {
//             if (!isLoggedIn || !userId) {
//                 setRatingMode("tmdb");
//                 return;
//             }

//             try {
//                 const userRef = doc(db, "users", userId);
//                 const userSnap = await getDoc(userRef);

//                 if (!userSnap.exists()) return;

//                 const savedRatingSource = userSnap.data()?.preferences?.ratingSource;

//                 if (isValidRatingMode(savedRatingSource)) {
//                     setRatingMode(savedRatingSource);
//                 }
//             } catch (error) {
//                 console.error("Erreur lors du chargement du mode de notation:", error);
//             }
//         };

//         loadRatingPreference();
//     }, [isLoggedIn, userId]);

//     useEffect(() => {
//         const loadMovieFrenzyRating = async () => {
//             if (!id) return;

//             try {
//                 const ratingRef = doc(db, "movieRatings", String(id));
//                 const ratingSnap = await getDoc(ratingRef);

//                 if (!ratingSnap.exists()) {
//                     setMovieFrenzyAverageRating(null);
//                     setMovieFrenzyRatingsCount(0);
//                     return;
//                 }

//                 const ratingData = ratingSnap.data();
//                 const averageRating = Number(ratingData.averageRating);
//                 const ratingsCount = Number(ratingData.ratingsCount || 0);

//                 if (Number.isFinite(averageRating) && ratingsCount > 0) {
//                     setMovieFrenzyAverageRating(averageRating);
//                     setMovieFrenzyRatingsCount(ratingsCount);
//                 } else {
//                     setMovieFrenzyAverageRating(null);
//                     setMovieFrenzyRatingsCount(0);
//                 }
//             } catch (error) {
//                 console.error("Erreur lors du chargement de la note MovieFrenzy:", error);
//                 setMovieFrenzyAverageRating(null);
//                 setMovieFrenzyRatingsCount(0);
//             }
//         };

//         loadMovieFrenzyRating();
//     }, [id]);

//     const cleanupOrphanMovieReviewReplies = useCallback(async (movieRatingKey, effectiveUserId) => {
//         if (!movieRatingKey || !effectiveUserId) return 0;

//         try {
//             const orphanRepliesQuery = query(
//                 collectionGroup(db, "replies"),
//                 where("movieRatingKey", "==", String(movieRatingKey))
//             );

//             const orphanRepliesSnap = await getDocs(orphanRepliesQuery);

//             const orphanReplyRefs = [];

//             await Promise.all(
//                 orphanRepliesSnap.docs.map(async (replyDocSnap) => {
//                     const replyData = replyDocSnap.data();

//                     const isCurrentUserMovieReview =
//                         replyData?.source === "movie_rating_review" &&
//                         replyData?.authorId === effectiveUserId;

//                     if (!isCurrentUserMovieReview) return;

//                     const parentPostRef = replyDocSnap.ref.parent.parent;

//                     if (!parentPostRef) {
//                         orphanReplyRefs.push(replyDocSnap.ref);
//                         return;
//                     }

//                     const parentPostSnap = await getDoc(parentPostRef);

//                     if (!parentPostSnap.exists()) {
//                         orphanReplyRefs.push(replyDocSnap.ref);
//                     }
//                 })
//             );

//             await Promise.all(
//                 orphanReplyRefs.map((replyRef) => deleteDoc(replyRef))
//             );

//             if (orphanReplyRefs.length > 0) {
//                 console.warn(
//                     `[MovieFrenzy] ${orphanReplyRefs.length} avis MovieFrenzy orphelin(s) supprimé(s).`
//                 );
//             }

//             return orphanReplyRefs.length;
//         } catch (error) {
//             console.error(
//                 "[MovieFrenzy] Erreur pendant le nettoyage des avis orphelins :",
//                 error
//             );

//             return 0;
//         }
//     }, []);

//     useEffect(() => {
//         const loadUserMovieRating = async () => {
//             const effectiveUserId = userId || auth.currentUser?.uid;

//             if (!isLoggedIn || !effectiveUserId || !id) {
//                 setUserMovieRating(null);
//                 setMovieReviewMeta(null);
//                 return;
//             }

//             setIsUserRatingLoading(true);

//             try {
//                 const movieRatingKey = String(id);
//                 await cleanupOrphanMovieReviewReplies(movieRatingKey, effectiveUserId);
//                 const userRatingRef = doc(
//                     db,
//                     "movieRatings",
//                     movieRatingKey,
//                     "userRatings",
//                     effectiveUserId
//                 );

//                 const userRatingSnap = await getDoc(userRatingRef);

//                 if (!userRatingSnap.exists()) {
//                     setUserMovieRating(null);
//                     setMovieReviewMeta(null);
//                     return;
//                 }

//                 const ratingData = userRatingSnap.data();
//                 const rating = Number(ratingData.rating);

//                 setUserMovieRating(Number.isFinite(rating) ? rating : null);

//                 const hasReview = ratingData.hasReview === true;
//                 const forumPostId = ratingData.forumPostId || null;
//                 const forumReplyId = ratingData.forumReplyId || null;

//                 if (!hasReview || !forumPostId || !forumReplyId) {
//                     setMovieReviewMeta(null);
//                     return;
//                 }

//                 const forumPostRef = doc(db, "posts", forumPostId);
//                 const forumReplyRef = doc(
//                     db,
//                     "posts",
//                     forumPostId,
//                     "replies",
//                     forumReplyId
//                 );

//                 const [forumPostSnap, forumReplySnap] = await Promise.all([
//                     getDoc(forumPostRef),
//                     getDoc(forumReplyRef),
//                 ]);

//                 if (!forumPostSnap.exists() || !forumReplySnap.exists()) {
//                     console.warn(
//                         "[MovieFrenzy] Avis orphelin détecté. Nettoyage des métadonnées utilisateur."
//                     );

//                     await updateDoc(userRatingRef, {
//                         hasReview: false,
//                         forumPostId: deleteField(),
//                         forumReplyId: deleteField(),
//                         reviewText: deleteField(),
//                         reviewUpdatedAt: deleteField(),
//                     });

//                     setMovieReviewMeta(null);
//                     return;
//                 }

//                 const replyData = forumReplySnap.data() || {};
//                 const reviewTextFromReply = extractReviewTextFromForumContent(
//                     replyData.content || ""
//                 );

//                 setMovieReviewMeta({
//                     hasReview: true,
//                     forumPostId,
//                     forumReplyId,
//                     reviewText: reviewTextFromReply || ratingData.reviewText || "",
//                 });
//             } catch (error) {
//                 console.error("Erreur lors du chargement de la note utilisateur:", error);
//                 setUserMovieRating(null);
//                 setMovieReviewMeta(null);
//             } finally {
//                 setIsUserRatingLoading(false);
//             }
//         };
//         loadUserMovieRating();
//     }, [isLoggedIn, userId, id, cleanupOrphanMovieReviewReplies]);

//     useEffect(() => {
//         if (userMovieRating !== null) {
//             setSelectedUserMovieRating(userMovieRating);
//             setUserRatingMessage("");
//         }
//     }, [userMovieRating]);

//     const videoOptions = {
//         height: '390',
//         width: '640',
//         playerVars: {
//             autoplay: 1,
//             color: "white",
//             controls: 1,
//             modestbranding: 1,
//             rel: 0,
//             fs: 1,

//             // Langue de l’interface interne du lecteur YouTube.
//             hl: currentLanguage,

//             // Langue préférée si le spectateur active lui-même les sous-titres.
//             cc_lang_pref: currentLanguage,

//             // Laisser désactivé pour ne pas forcer les sous-titres au démarrage.
//             // cc_load_policy: 1,

//             iv_load_policy: 3,
//             enablejsapi: 1,
//             origin: window.location.origin,
//         }
//     };

//     const resolveKinoFallback = useCallback(async (candidates, videoLoad) => {
//         if (
//             videoLoadRef.current !== videoLoad ||
//             candidates.trailer.length > 0 ||
//             videoLoad.kinoAttempted
//         ) {
//             return candidates;
//         }

//         videoLoad.kinoAttempted = true;
//         videoLoad.kinoPending = true;
//         setIsKinoLoading(true);

//         try {
//             const videos = await fetchKinoCheckTrailers({
//                 tmdbId: videoLoad.movieId,
//                 language: videoLoad.language,
//             });

//             if (videoLoadRef.current !== videoLoad) return candidates;

//             // Éviter les vidéos déjà rejetées ou déjà présentes
//             // dans une autre catégorie, notamment les Bonus.
//             const excludedKeys = new Set([
//                 ...rejectedVideoKeysRef.current,
//                 ...Object.values(candidates).flat().map((video) => video.key),
//             ]);

//             const availableVideos = videos.filter(
//                 (video) => !excludedKeys.has(video.key)
//             );

//             const ranked = rankVideoCandidates(
//                 availableVideos,
//                 videoLoad.language
//             );

//             return { ...candidates, trailer: ranked.main };
//         } finally {
//             videoLoad.kinoPending = false;

//             if (videoLoadRef.current === videoLoad) {
//                 setIsKinoLoading(false);
//             }
//         }
//     }, []);

//     const _onReady = (event) => {
//         event.target.playVideo();
//     };

//     const _onError = async (event) => {
//         const videoLoad = videoLoadRef.current;

//         if (
//             !videoLoad ||
//             videoLoad.movieId !== id ||
//             videoLoad.language !== currentLanguage ||
//             videoLoad.kinoPending
//         ) {
//             return;
//         }

//         const reportedVideoKey = event.target?.getVideoData?.()?.video_id;
//         const failedVideoKey = reportedVideoKey || videoKey;

//         console.warn("[MovieFrenzy YouTube]", {
//             errorCode: event.data,
//             videoKey: failedVideoKey,
//         });

//         if (
//             ![5, 100, 101, 150].includes(event.data) ||
//             !failedVideoKey ||
//             failedVideoKey !== videoKey ||
//             !videoCandidates.some((video) => video.key === failedVideoKey) ||
//             rejectedVideoKeysRef.current.has(failedVideoKey)
//         ) {
//             return;
//         }

//         rejectedVideoKeysRef.current.add(failedVideoKey);

//         const remainingCandidates = Object.fromEntries(
//             videoCategoryOrder.map((category) => [
//                 category,
//                 videoCandidatesByCategory[category].filter(
//                     (video) => !rejectedVideoKeysRef.current.has(video.key)
//                 ),
//             ])
//         );

//         setVideoKey("");
//         setVideoCandidatesByCategory(remainingCandidates);

//         const nextCandidates = await resolveKinoFallback(
//             remainingCandidates,
//             videoLoad
//         );

//         if (videoLoadRef.current !== videoLoad) return;

//         const nextCategory =
//             nextCandidates[activeVideoCategory].length > 0
//                 ? activeVideoCategory
//                 : videoCategoryOrder.find(
//                     (category) => nextCandidates[category].length > 0
//                 ) || "trailer";

//         setVideoCandidatesByCategory(nextCandidates);
//         setActiveVideoCategory(nextCategory);
//         setVideoKey(nextCandidates[nextCategory][0]?.key || "");
//     };

//     const handleVideoCategoryChange = (category) => {
//         if (videoLoadRef.current?.kinoPending) return;

//         // La suite actuelle de ta fonction...
//         if (category === activeVideoCategory) {
//             return;
//         }

//         const categoryCandidates =
//             videoCandidatesByCategory[category] || [];

//         if (categoryCandidates.length === 0) {
//             return;
//         }

//         const firstAvailableVideo = categoryCandidates.find(
//             (candidate) =>
//                 !rejectedVideoKeysRef.current.has(candidate.key)
//         );

//         setActiveVideoCategory(category);
//         setVideoKey(firstAvailableVideo?.key || "");
//     };

//     const handleActeurClick = (acteurId) => {
//         history(`/acteurDetails/${acteurId}`);
//     };

//     const handleCategoryClick = (categoryId) => {
//         history(`/categories/${categoryId}`);
//     };



//     // Fonction mémorisée pour récupérer les détails du film, les crédits et la bande-annonce
//     const fetchAllMovieDetails = useCallback(async () => {
//         const videoLoad = {
//             movieId: id,
//             language: currentLanguage,
//             kinoAttempted: false,
//             kinoPending: false,
//         };

//         videoLoadRef.current = videoLoad;
//         rejectedVideoKeysRef.current = new Set();
//         setIsKinoLoading(false);
//         setVideoKey("");
//         setActiveVideoCategory("trailer");
//         setVideoCandidatesByCategory({
//             trailer: [],
//             behindScenes: [],
//             bonus: [],
//         });
//         try {
//             const tmdbLocale = getTmdbLocale(currentLanguage);

//             if (!TMDB_API_KEY) {
//                 throw new Error(
//                     "La variable REACT_APP_TMDB_API_KEY est absente."
//                 );
//             }

//             setMaturityRating(null);
//             setQuebecProductionInfo(null);
//             setOmdbData(null);
//             const [
//                 detailResponse,
//                 creditsResponse,
//                 videoResponse,
//                 ageRatingResponse
//             ] = await Promise.all([
//                 axios.get(
//                     `https://api.themoviedb.org/3/movie/${id}?api_key=${TMDB_API_KEY}&language=${tmdbLocale}`
//                 ),
//                 axios.get(
//                     `https://api.themoviedb.org/3/movie/${id}/credits?api_key=${TMDB_API_KEY}&language=${tmdbLocale}`
//                 ),
//                 axios.get(
//                     `https://api.themoviedb.org/3/movie/${id}/videos?api_key=${TMDB_API_KEY}&language=${tmdbLocale}`
//                 ),
//                 axios.get(
//                     `https://api.themoviedb.org/3/movie/${id}/release_dates?api_key=${TMDB_API_KEY}&language=${tmdbLocale}`
//                 )
//             ]);

//             let movieDetails = detailResponse.data;
//             const movieCredits = creditsResponse.data;
//             const movieVideos = videoResponse.data.results;
//             const movieAgeRatings = ageRatingResponse.data.results;

//             /*
//  * Lorsqu’aucun synopsis n’existe dans la langue choisie,
//  * tenter uniquement le synopsis anglais sans remplacer
//  * les autres renseignements localisés du film.
//  */
//             if (
//                 !String(movieDetails.overview || "").trim() &&
//                 tmdbLocale !== "en-US"
//             ) {
//                 try {
//                     const englishDetailResponse = await axios.get(
//                         `https://api.themoviedb.org/3/movie/${id}?api_key=${TMDB_API_KEY}&language=en-US`
//                     );

//                     movieDetails = {
//                         ...movieDetails,
//                         overview: String(
//                             englishDetailResponse.data?.overview || ""
//                         ).trim(),
//                     };
//                 } catch (error) {
//                     console.warn(
//                         "[MovieFrenzy] Synopsis anglais de relève indisponible.",
//                         error
//                     );
//                 }
//             }

//             if (videoLoadRef.current !== videoLoad) return;

//             // Logique pour mettre à jour l'état avec les détails du film
//             setDetail(movieDetails);
//             setGenres(movieDetails.genres);
//             setActors(movieCredits.cast);

//             const omdbRequest = fetchOmdbMovieData({
//                 imdbId: movieDetails.imdb_id,

//                 title:
//                     movieDetails.original_title ||
//                     movieDetails.title,

//                 year:
//                     movieDetails.release_date?.slice(0, 4),
//             });

//             // Ordre de priorité : langue choisie,
//             // langue originale du film, puis anglais.
//             const languagePriority = [
//                 currentLanguage,
//                 movieDetails.original_language,
//                 "en",
//             ].filter((language, index, languages) => {
//                 return (
//                     Boolean(language) &&
//                     languages.indexOf(language) === index
//                 );
//             });

//             // Récupérer et classer tous les candidats pour permettre
//             // une relève automatique lorsqu’une vidéo refuse l’intégration.
//             const candidateGroups = await Promise.all(
//                 languagePriority.map(async (candidateLanguage) => {
//                     try {
//                         let candidateVideos = movieVideos;

//                         // La première réponse a déjà été obtenue dans Promise.all.
//                         // On appelle TMDB uniquement pour les langues de repli.
//                         if (candidateLanguage !== currentLanguage) {
//                             const candidateLocale =
//                                 getTmdbLocale(candidateLanguage);

//                             const fallbackVideoResponse = await axios.get(
//                                 `https://api.themoviedb.org/3/movie/${id}/videos?api_key=${TMDB_API_KEY}&language=${candidateLocale}`
//                             );

//                             candidateVideos =
//                                 fallbackVideoResponse.data.results;
//                         }

//                         const rankedCandidates = rankVideoCandidates(
//                             candidateVideos,
//                             candidateLanguage
//                         );

//                         const addCandidateMetadata = (
//                             videos,
//                             candidateLevel
//                         ) => {
//                             return videos.map((video) => ({
//                                 ...video,
//                                 movieFrenzyLanguage: candidateLanguage,
//                                 movieFrenzyCandidateLevel: candidateLevel,
//                             }));
//                         };

//                         return {
//                             main: addCandidateMetadata(
//                                 rankedCandidates.main,
//                                 "main"
//                             ),

//                             backup: addCandidateMetadata(
//                                 rankedCandidates.backup,
//                                 "backup"
//                             ),

//                             behindScenes: addCandidateMetadata(
//                                 rankedCandidates.behindScenes,
//                                 "standard"
//                             ),

//                             bonus: addCandidateMetadata(
//                                 rankedCandidates.bonus,
//                                 "standard"
//                             ),
//                         };
//                     } catch (error) {
//                         console.warn(
//                             `[MovieFrenzy] Impossible de récupérer les vidéos ${candidateLanguage}`,
//                             error
//                         );

//                         return {
//                             main: [],
//                             backup: [],
//                             behindScenes: [],
//                             bonus: [],
//                         };
//                     }
//                 })
//             );

//             // Récupérer les vidéos de relève propres à MovieFrenzy.
//             // Elles passeront après toutes les vidéos obtenues auprès de TMDB.
//             const rankedMovieFrenzyFallbacks =
//                 rankVideoCandidates(
//                     getMovieVideoFallbacks(id),
//                     currentLanguage
//                 );

//             const addFallbackMetadata = (videos = []) => {
//                 return videos.map((video) => ({
//                     ...video,
//                     movieFrenzyLanguage:
//                         video.iso_639_1 || currentLanguage,

//                     movieFrenzyCandidateLevel: "fallback",
//                 }));
//             };

//             const movieFrenzyFallbackGroup = {
//                 main: addFallbackMetadata(
//                     rankedMovieFrenzyFallbacks.main
//                 ),

//                 backup: addFallbackMetadata(
//                     rankedMovieFrenzyFallbacks.backup
//                 ),

//                 behindScenes: addFallbackMetadata(
//                     rankedMovieFrenzyFallbacks.behindScenes
//                 ),

//                 bonus: addFallbackMetadata(
//                     rankedMovieFrenzyFallbacks.bonus
//                 ),
//             };

//             // Les groupes TMDB restent prioritaires.
//             // Le groupe MovieFrenzy intervient en dernier recours.
//             const allCandidateGroups = [
//                 ...candidateGroups,
//                 movieFrenzyFallbackGroup,
//             ];

//             const removeDuplicateVideos = (videos = []) => {
//                 return videos.filter(
//                     (video, index, allVideos) =>
//                         allVideos.findIndex(
//                             (candidate) =>
//                                 candidate.key === video.key
//                         ) === index
//                 );
//             };

//             const mainTrailerCandidates =
//                 allCandidateGroups.flatMap(
//                     (group) => group.main
//                 );

//             const backupTrailerCandidates =
//                 allCandidateGroups.flatMap(
//                     (group) => group.backup
//                 );

//             const candidatesByCategory = {
//                 trailer: removeDuplicateVideos([
//                     ...mainTrailerCandidates,
//                     ...backupTrailerCandidates,
//                 ]),

//                 behindScenes: removeDuplicateVideos(
//                     allCandidateGroups.flatMap(
//                         (group) => group.behindScenes
//                     )
//                 ),

//                 bonus: removeDuplicateVideos(
//                     allCandidateGroups.flatMap(
//                         (group) => group.bonus
//                     )
//                 ),
//             };

//             const resolvedCandidates = await resolveKinoFallback(
//                 candidatesByCategory,
//                 videoLoad
//             );

//             if (videoLoadRef.current !== videoLoad) return;

//             const initialVideoCategory =
//                 ["trailer", "behindScenes", "bonus"].find(
//                     (category) => resolvedCandidates[category].length > 0
//                 ) || "trailer";

//             setVideoCandidatesByCategory(resolvedCandidates);
//             setActiveVideoCategory(initialVideoCategory);
//             setVideoKey(
//                 resolvedCandidates[initialVideoCategory][0]?.key || ""
//             );


//             // ======================================================
//             // PERSONNALITÉS — RÉALISATEURS ET SCÉNARISTE
//             // ======================================================

//             const movieCrew = Array.isArray(movieCredits.crew)
//                 ? movieCredits.crew
//                 : [];

//             // Un film peut posséder plusieurs réalisateurs.
//             // On conserve leurs objets TMDB complets, notamment leur id.
//             const movieDirectors = movieCrew
//                 .filter(
//                     (crewMember) =>
//                         crewMember.job?.toLowerCase() === "director"
//                 )
//                 .filter(
//                     (director, index, allDirectors) =>
//                         allDirectors.findIndex(
//                             (candidate) =>
//                                 candidate.id === director.id
//                         ) === index
//                 );

//             setDirectors(movieDirectors);

//             // ======================================================
//             // ORIGINE QUÉBÉCOISE DU FILM
//             // ======================================================

//             const nextQuebecProductionInfo =
//                 await findQuebecProduction({
//                     tmdbId: movieDetails.id,

//                     title:
//                         movieDetails.title,

//                     originalTitle:
//                         movieDetails.original_title,

//                     releaseDate:
//                         movieDetails.release_date,

//                     directors:
//                         movieDirectors,
//                 });

//             setQuebecProductionInfo(
//                 nextQuebecProductionInfo
//             );

//             // Pour le moment, MovieFrenzy conserve un seul scénariste.
//             const writer = movieCrew.find(
//                 (crewMember) =>
//                     crewMember.job?.toLowerCase() === "screenplay" ||
//                     crewMember.job?.toLowerCase() === "writer"
//             );

//             setDetail((prevDetail) => ({
//                 ...prevDetail,
//                 writer: writer?.name || "",
//             }));

//             // ======================================================
//             // CLASSEMENT DE MATURITÉ
//             // ======================================================

//             let nextMaturityRating = null;

//             /*
//              * Lorsqu’un membre réside au Québec, MovieFrenzy
//              * cherche d’abord le classement officiel québécois.
//              */
//             if (ratingSystem === "QC") {
//                 const officialQuebecRating =
//                     await findQuebecRating({
//                         tmdbId: movieDetails.id,
//                         title: movieDetails.title,
//                         originalTitle:
//                             movieDetails.original_title,
//                         releaseDate:
//                             movieDetails.release_date,
//                         directors: movieDirectors,
//                     });

//                 if (officialQuebecRating) {
//                     const officialCode =
//                         normalizeCertificationCode(
//                             officialQuebecRating.rating
//                         );

//                     nextMaturityRating = {
//                         system: "QC",
//                         sourceCountry: "QC",
//                         source: "quebec-official",

//                         code: officialCode,

//                         label: formatCertificationLabel({
//                             system: "QC",
//                             code: officialCode,
//                             t,
//                         }),

//                         advisories:
//                             officialQuebecRating.advisories || [],

//                         nations:
//                             officialQuebecRating.nations || [],

//                         sourceUrl:
//                             officialQuebecRating.sourceUrl || "",

//                         quebecFilmId:
//                             officialQuebecRating.quebecFilmId ||
//                             null,
//                     };
//                 }
//             }

//             /*
//              * Si le film n’est pas retrouvé dans l’index québécois,
//              * conserver le système de repli déjà en place avec TMDB.
//              */
//             if (!nextMaturityRating) {
//                 nextMaturityRating =
//                     buildMaturityRating({
//                         movieAgeRatings,
//                         ratingSystem,
//                         language: currentLanguage,
//                         t,
//                     });
//             }

//             setMaturityRating(nextMaturityRating);

//             const omdbMovieData =
//                 await omdbRequest;

//             setOmdbData(omdbMovieData);

//         } catch (error) {
//             console.log(error);
//         }
//     }, [
//         id,
//         currentLanguage,
//         ratingSystem,
//         t,
//         resolveKinoFallback,
//     ]);// Les dépendances de useCallback

//     // Utilisation de la fonction mémorisée comme dépendance de useEffect
//     useEffect(() => {
//         fetchAllMovieDetails();

//         return () => {
//             videoLoadRef.current = null;
//         };
//     }, [fetchAllMovieDetails]);


//     useEffect(() => {
//         const checkFavoriteStatus = async () => {
//             if (isLoggedIn && userId && detail.id) {
//                 try {
//                     const userRef = doc(db, "users", userId);
//                     const userSnap = await getDoc(userRef);
//                     if (userSnap.exists()) {
//                         const userData = userSnap.data();
//                         const currentFavorites = userData.favoritePreviews || [];
//                         if (currentFavorites.some(movie => movie.id === detail.id)) {
//                             // setIsFavorite(true); // This line was removed
//                         }
//                     }
//                 } catch (error) {
//                     console.error("Error checking favorite status:", error);
//                 }
//             }
//         };
//         checkFavoriteStatus();
//     }, [isLoggedIn, userId, detail.id]);


//     const renderGenres = (genreIds) => {
//         return genreIds.map((id) => {
//             const genre = genres.find((g) => g.id === id);
//             if (genre) {
//                 return <Genre key={id} id={genre.id} name={genre.name} />;
//             }
//             return null;
//         });
//     };


//     // Doughnut chart data and options
//     useEffect(() => {
//         setChartData({
//             datasets: [
//                 {
//                     data: [activeChartPercent, 100 - activeChartPercent],
//                     backgroundColor: activeChartColors,
//                     borderWidth: 0,
//                 },
//             ],
//         });
//     }, [activeChartPercent, activeChartColors]);

//     const chartOptions = {
//         plugins: {
//             legend: {
//                 display: false,
//             },
//             tooltip: {
//                 enabled: activeRatingPercent !== null,
//             },
//         },
//         onHover: (event, chartElement, chart) => {
//             // Restore original styles if no element is hovered
//             if (chartElement.length === 0) {
//                 chart.data.datasets[0].backgroundColor = activeChartColors;
//                 setChartTextStyle({});
//                 chart.update();
//                 return;
//             }

//             const element = chartElement[0];
//             // If hovering over the pink part (index 0)
//             if (element.index === 0) {
//                 chart.data.datasets[0].backgroundColor = activeChartHoverColors;
//                 setChartTextStyle({
//                     // transform: 'scale(1.1)',
//                     color: '#00FFFF', // Cyan color
//                 });
//             } else {
//                 // Optional: handle hover on the grey part
//                 chart.data.datasets[0].backgroundColor = activeChartColors;
//                 setChartTextStyle({});
//             }
//             chart.update();
//         },
//         animation: {
//             duration: isVisible ? 1000 : 0,
//         },
//     };

//     useEffect(() => {
//         window.scrollTo(0, 0);

//         const chartElement =
//             document.querySelector(".chart-container");

//         const overviewSectionElement =
//             overviewSectionRef.current;

//         /*
//          * Solution de secours pour les navigateurs
//          * ne prenant pas en charge IntersectionObserver.
//          */
//         if (!("IntersectionObserver" in window)) {
//             setIsVisible(true);

//             overviewSectionElement?.classList.add(
//                 "is-overview-visible"
//             );

//             return;
//         }

//         const chartObserver = new IntersectionObserver(
//             (entries) => {
//                 entries.forEach((entry) => {
//                     if (entry.isIntersecting) {
//                         setIsVisible(true);
//                         chartObserver.unobserve(entry.target);
//                     }
//                 });
//             },
//             {
//                 threshold: 1,
//             }
//         );

//         const overviewObserver = new IntersectionObserver(
//             (entries) => {
//                 entries.forEach((entry) => {
//                     if (!entry.isIntersecting) {
//                         return;
//                     }

//                     entry.target.classList.add(
//                         "is-overview-visible"
//                     );

//                     overviewObserver.unobserve(entry.target);
//                 });
//             },
//             {
//                 threshold: 0.3,
//                 rootMargin: "0px 0px -8% 0px",
//             }
//         );

//         if (chartElement) {
//             chartObserver.observe(chartElement);
//         }

//         if (overviewSectionElement) {
//             /*
//              * Permet de rejouer l’entrée lorsqu’on change
//              * de film sans démonter complètement la page.
//              */
//             overviewSectionElement.classList.remove(
//                 "is-overview-visible"
//             );

//             overviewObserver.observe(
//                 overviewSectionElement
//             );
//         }

//         return () => {
//             chartObserver.disconnect();
//             overviewObserver.disconnect();
//         };
//     }, [id]);

//     useEffect(() => {
//         if (!isVisible || activeRatingPercent === null || activeChartPercent <= 0) {
//             setAnimatedPercent("0.00");
//             return;
//         }

//         let start = null;
//         const duration = 1000;

//         const step = (timestamp) => {
//             if (!start) start = timestamp;
//             const progress = Math.min((timestamp - start) / duration, 1);
//             const currentPercent = progress * activeChartPercent;
//             setAnimatedPercent(currentPercent.toFixed(2));

//             if (progress < 1) {
//                 window.requestAnimationFrame(step);
//             } else {
//                 setAnimatedPercent(Number(activeRatingPercent).toFixed(2));
//             }
//         };

//         window.requestAnimationFrame(step);
//     }, [isVisible, activeRatingPercent, activeChartPercent]);


//     const addFavoritePreview = async () => {
//         try {
//             if (!isLoggedIn || !detail) return;

//             const effectiveUserId = auth.currentUser?.uid;

//             if (!effectiveUserId) {
//                 console.error("Aucun utilisateur connecté.");
//                 return;
//             }

//             const userRef = doc(db, "users", effectiveUserId);
//             const userSnap = await getDoc(userRef);

//             if (!userSnap.exists()) {
//                 console.error("No such user!");
//                 return;
//             }

//             const userData = userSnap.data();
//             const currentFavorites = userData.favoritePreviews || [];

//             if (currentFavorites.some(movie => movie.id === detail.id)) {
//                 setShowAlreadyInFavorites(true);
//                 setTimeout(() => {
//                     setShowAlreadyInFavorites(false);
//                 }, 3000);
//                 return;
//             }

//             if (currentFavorites.length >= 3) {
//                 setShowLimitMessage(true);
//                 setTimeout(() => {
//                     setShowLimitMessage(false);
//                 }, 3000);
//                 return;
//             }

//             const newFavoritePreviews = [...currentFavorites, detail];
//             const nextFavoriteMoviesCount = Math.min(newFavoritePreviews.length, 3);

//             await updateDoc(userRef, {
//                 favoritePreviews: newFavoritePreviews,
//                 "xp.sources.favoriteMovies": nextFavoriteMoviesCount,
//                 "xp.updatedAt": serverTimestamp(),
//             });

//             setActivityGainToast({
//                 id: `favorite-movie-${detail.id}-${Date.now()}`,
//                 points: "+1.5",
//                 unitLabel: t("profilMember.activity"),
//                 label: "",
//                 variant: "favorite",
//             });

//             setShowConfirmation(true);
//             setTimeout(() => {
//                 setShowConfirmation(false);
//             }, 3000);
//         } catch (error) {
//             console.error("Error adding favorite preview:", error);
//         }
//     };

//     const handleSaveUserMovieRating = async () => {
//         const effectiveUserId = userId || auth.currentUser?.uid;

//         if (!isLoggedIn || !effectiveUserId || !id) {
//             setUserRatingMessage(t("movie.rating.loginToSaveRating"));
//             return;
//         }

//         const rating = Number(selectedUserMovieRating);

//         if (!Number.isFinite(rating) || rating < 1 || rating > 10) {
//             setUserRatingMessage(t("movie.rating.invalidRating"));
//             return;
//         }

//         setIsSavingUserRating(true);
//         setUserRatingMessage("");

//         try {
//             const movieRatingRef = doc(db, "movieRatings", String(id));
//             const userRatingRef = doc(
//                 db,
//                 "movieRatings",
//                 String(id),
//                 "userRatings",
//                 effectiveUserId
//             );

//             const userRef = doc(db, "users", effectiveUserId);

//             const result = await runTransaction(db, async (transaction) => {
//                 const movieRatingSnap = await transaction.get(movieRatingRef);
//                 const userRatingSnap = await transaction.get(userRatingRef);

//                 const movieRatingData = movieRatingSnap.exists()
//                     ? movieRatingSnap.data()
//                     : {};

//                 const previousRating = userRatingSnap.exists()
//                     ? Number(userRatingSnap.data().rating)
//                     : null;

//                 const hasPreviousRating = Number.isFinite(previousRating);

//                 const currentTotal = Number(movieRatingData.totalRating || 0);
//                 const currentCount = Number(movieRatingData.ratingsCount || 0);

//                 let nextTotal;
//                 let nextCount;

//                 if (hasPreviousRating && currentCount <= 1) {
//                     nextTotal = rating;
//                     nextCount = 1;
//                 } else if (hasPreviousRating && currentCount > 1) {
//                     nextTotal = currentTotal - previousRating + rating;
//                     nextCount = currentCount;
//                 } else {
//                     nextTotal = currentTotal + rating;
//                     nextCount = currentCount + 1;
//                 }

//                 const safeTotal = Number(nextTotal.toFixed(2));
//                 const nextAverage = Number((safeTotal / nextCount).toFixed(2));

//                 transaction.set(
//                     movieRatingRef,
//                     {
//                         averageRating: nextAverage,
//                         ratingsCount: nextCount,
//                         totalRating: safeTotal,
//                         tmdbId: Number(id),
//                         title: detail.title || movieRatingData.title || "",
//                         updatedAt: serverTimestamp(),
//                     },
//                     { merge: true }
//                 );

//                 transaction.set(
//                     userRatingRef,
//                     {
//                         rating,
//                         updatedAt: serverTimestamp(),
//                         ...(hasPreviousRating ? {} : { createdAt: serverTimestamp() }),
//                     },
//                     { merge: true }
//                 );
//                 if (!hasPreviousRating) {
//                     transaction.update(userRef, {
//                         "xp.sources.movieRatings": increment(1),
//                         "xp.updatedAt": serverTimestamp(),
//                     });
//                 }

//                 return {
//                     averageRating: nextAverage,
//                     ratingsCount: nextCount,
//                     totalRating: safeTotal,
//                     isFirstRating: !hasPreviousRating,
//                 };
//             });

//             setUserMovieRating(rating);
//             setMovieFrenzyAverageRating(result.averageRating);
//             setMovieFrenzyRatingsCount(result.ratingsCount);
//             setUserRatingMessage(
//                 t("movie.rating.ratingSaved").replace("{{rating}}", rating)
//             );
//             if (result.isFirstRating) {
//                 setActivityGainToast({
//                     id: `movie-rating-${id}-${Date.now()}`,
//                     points: "+0.5",
//                     unitLabel: t("profilMember.activity"),
//                     label: t("activityToast.movieRatingFirst"),
//                     variant: "rating",
//                 });
//             }
//         } catch (error) {
//             console.error("Erreur lors de l’enregistrement de la note utilisateur:", error);
//             setUserRatingMessage(t("movie.rating.ratingSaveError"));
//         } finally {
//             setIsSavingUserRating(false);
//         }
//     };

//     const handleViewMovieReview = () => {
//         if (!movieReviewMeta?.forumPostId) return;

//         history(`/subject/${movieReviewMeta.forumPostId}`);
//     };

//     const handleOpenMovieReview = async () => {
//         let initialDraft = "";

//         if (
//             hasExistingMovieReview &&
//             movieReviewMeta?.forumPostId &&
//             movieReviewMeta?.forumReplyId
//         ) {
//             try {
//                 const existingReplyRef = doc(
//                     db,
//                     "posts",
//                     movieReviewMeta.forumPostId,
//                     "replies",
//                     movieReviewMeta.forumReplyId
//                 );

//                 const existingReplySnap = await getDoc(existingReplyRef);

//                 if (existingReplySnap.exists()) {
//                     const existingReplyData = existingReplySnap.data();

//                     initialDraft = extractReviewTextFromForumContent(
//                         existingReplyData.content || ""
//                     );
//                 } else {
//                     initialDraft = movieReviewMeta?.reviewText || "";
//                 }
//             } catch (error) {
//                 console.error(
//                     "[MovieFrenzy] Erreur lors du chargement de l'avis existant :",
//                     error
//                 );

//                 initialDraft = movieReviewMeta?.reviewText || "";
//             }
//         }

//         setMovieReviewDraft(initialDraft);
//         setIsMovieReviewModalOpen(true);

//         console.log("[MovieFrenzy] Ouvrir le modal d'avis", {
//             movieId: id,
//             title: detail.title,
//             userRating: userMovieRating,
//             hasExistingMovieReview,
//             initialDraftLength: initialDraft.length,
//         });
//     };

//     const handleDeleteMovieReview = async () => {
//         const effectiveUserId = userId || auth.currentUser?.uid;
//         const forumPostId = movieReviewMeta?.forumPostId;
//         const forumReplyId = movieReviewMeta?.forumReplyId;

//         if (!effectiveUserId || !id || !forumPostId || !forumReplyId) {
//             console.warn("Suppression impossible : métadonnées d'avis MovieFrenzy incomplètes.");
//             return;
//         }

//         const confirmed = window.confirm(
//             t("movie.rating.deleteReviewConfirm")
//         );

//         if (!confirmed) return;

//         setIsDeletingMovieReview(true);

//         let deletedForumPost = false;

//         try {
//             const postRef = doc(db, "posts", forumPostId);
//             const replyRef = doc(db, "posts", forumPostId, "replies", forumReplyId);
//             const userRatingRef = doc(
//                 db,
//                 "movieRatings",
//                 String(id),
//                 "userRatings",
//                 effectiveUserId
//             );
//             const userRef = doc(db, "users", effectiveUserId);

//             const profileReviewRef = doc(
//                 db,
//                 "users",
//                 effectiveUserId,
//                 "movieReviews",
//                 String(id)
//             );

//             await runTransaction(db, async (transaction) => {
//                 const postSnap = await transaction.get(postRef);

//                 const postData = postSnap.exists() ? postSnap.data() : {};
//                 const currentReplies = Number(postData?.replies) || 0;
//                 const nextReplies = Math.max(0, currentReplies - 1);

//                 const isMovieReviewTopic =
//                     postData?.type === "movie_rating_discussion" ||
//                     postData?.source === "movie_rating_review";

//                 transaction.delete(replyRef);
//                 transaction.delete(profileReviewRef);

//                 if (postSnap.exists()) {
//                     if (isMovieReviewTopic && nextReplies === 0) {
//                         transaction.delete(postRef);
//                         deletedForumPost = true;
//                     } else {
//                         transaction.update(postRef, {
//                             replies: nextReplies,
//                         });
//                     }
//                 }

//                 transaction.update(userRatingRef, {
//                     hasReview: false,
//                     forumPostId: deleteField(),
//                     forumReplyId: deleteField(),
//                     reviewText: deleteField(),
//                     reviewUpdatedAt: deleteField(),
//                 });
//                 transaction.update(userRef, {
//                     "xp.sources.movieReviews": increment(-1),
//                     "xp.updatedAt": serverTimestamp(),
//                 });
//             });

//             setMovieReviewMeta(null);
//             setMovieReviewDraft("");

//             if (deletedForumPost) {
//                 setMovieReviewForumPostId(null);
//                 setMovieReviewRepliesCount(0);
//             } else {
//                 setMovieReviewRepliesCount((count) =>
//                     Math.max(0, Number(count || 0) - 1)
//                 );
//             }

//             setIsMovieReviewModalOpen(false);
//         } catch (error) {
//             console.error("Erreur lors de la suppression de l'avis MovieFrenzy :", error);
//         } finally {
//             setIsDeletingMovieReview(false);
//         }
//     };

//     const handleCloseMovieReview = () => {
//         setIsMovieReviewModalOpen(false);
//         setMovieReviewDraft("");
//     };

//     const handleSubmitMovieReviewDraft = async () => {
//         const trimmedReview = movieReviewDraft.trim();
//         const effectiveUserId = userId || auth.currentUser?.uid;

//         if (trimmedReview.length < 3) {
//             return;
//         }

//         if (!isLoggedIn || !effectiveUserId || !id) {
//             console.error("[MovieFrenzy] Impossible de publier l'avis : utilisateur ou film manquant.");
//             return;
//         }

//         try {
//             const movieRatingKey = String(id);
//             const movieTitle = detail.title || "ce film";

//             const userRatingRef = doc(
//                 db,
//                 "movieRatings",
//                 movieRatingKey,
//                 "userRatings",
//                 effectiveUserId
//             );

//             const userRef = doc(db, "users", effectiveUserId);

//             const profileReviewRef = doc(
//                 db,
//                 "users",
//                 effectiveUserId,
//                 "movieReviews",
//                 movieRatingKey
//             );

//             const buildProfileReviewData = ({ forumPostId, forumReplyId }) => ({
//                 tmdbId: Number(id),
//                 movieRatingKey,
//                 movieTitle,
//                 moviePosterPath: detail.poster_path || "",
//                 movieBackdropPath: detail.backdrop_path || "",
//                 rating: Number(userMovieRating),
//                 reviewText: trimmedReview,
//                 reviewExcerpt: createMovieReviewExcerpt(trimmedReview),
//                 forumPostId,
//                 forumReplyId,
//                 authorId: effectiveUserId,
//                 source: "movie_rating_review",
//                 updatedAt: serverTimestamp(),
//             });

//             if (hasExistingMovieReview) {
//                 const existingReplyRef = doc(
//                     db,
//                     "posts",
//                     movieReviewMeta.forumPostId,
//                     "replies",
//                     movieReviewMeta.forumReplyId
//                 );
//                 const existingForumPostRef = doc(
//                     db,
//                     "posts",
//                     movieReviewMeta.forumPostId
//                 );

//                 const profileReviewSnap = await getDoc(profileReviewRef);
//                 const updateBatch = writeBatch(db);

//                 updateBatch.update(existingReplyRef, {
//                     content: formatMovieReviewContent({
//                         movieTitle,
//                         rating: userMovieRating,
//                         review: trimmedReview,
//                     }),
//                     movieRating: Number(userMovieRating),
//                     movieTitle,
//                     updatedAt: serverTimestamp(),
//                 });

//                 updateBatch.update(existingForumPostRef, {
//                     moviePosterPath: detail.poster_path || "",
//                     movieBackdropPath: detail.backdrop_path || "",
//                     movieTitle,
//                     tmdbId: Number(id),
//                     movieRatingKey,
//                     source: "movie_rating_review",
//                     type: "movie_rating_discussion",
//                 });

//                 updateBatch.update(userRatingRef, {
//                     reviewText: trimmedReview,
//                     reviewUpdatedAt: serverTimestamp(),
//                 });

//                 updateBatch.set(
//                     profileReviewRef,
//                     {
//                         ...buildProfileReviewData({
//                             forumPostId: movieReviewMeta.forumPostId,
//                             forumReplyId: movieReviewMeta.forumReplyId,
//                         }),

//                         ...(profileReviewSnap.exists()
//                             ? {}
//                             : {
//                                 createdAt: serverTimestamp(),
//                                 visibleOnProfile: true,
//                             }),
//                     },
//                     { merge: true }
//                 );

//                 await updateBatch.commit();

//                 setMovieReviewMeta((prevMeta) => ({
//                     ...prevMeta,
//                     reviewText: trimmedReview,
//                 }));

//                 console.log("[MovieFrenzy] Avis MovieFrenzy modifié", {
//                     forumPostId: movieReviewMeta.forumPostId,
//                     forumReplyId: movieReviewMeta.forumReplyId,
//                     movieRatingKey,
//                     movieTitle,
//                 });

//                 setIsMovieReviewModalOpen(false);
//                 setMovieReviewDraft("");
//                 history(`/subject/${movieReviewMeta.forumPostId}`);
//                 return;
//             }

//             let forumPostId = null;

//             const existingForumTopicQuery = query(
//                 collection(db, "posts"),
//                 where("movieRatingKey", "==", movieRatingKey),
//                 limit(1)
//             );

//             const existingForumTopicSnap = await getDocs(existingForumTopicQuery);

//             if (!existingForumTopicSnap.empty) {
//                 const existingForumTopicDoc = existingForumTopicSnap.docs[0];
//                 const existingForumTopicData = existingForumTopicDoc.data();

//                 forumPostId = existingForumTopicDoc.id;

//                 if (!existingForumTopicData.moviePosterPath && detail.poster_path) {
//                     await updateDoc(doc(db, "posts", forumPostId), {
//                         moviePosterPath: detail.poster_path || "",
//                         movieBackdropPath: detail.backdrop_path || "",
//                         movieTitle,
//                         tmdbId: Number(id),
//                     });
//                 }
//             } else {
//                 const forumTopicRef = await addDoc(collection(db, "posts"), {
//                     title: `Avis des cinéphiles sur ${movieTitle}`,
//                     category: "autre",
//                     content: `<p>Discussion ouverte pour les avis MovieFrenzy sur <strong>${escapeHtml(movieTitle)}</strong>.</p>`,
//                     timestamp: serverTimestamp(),
//                     author: username || "Anonyme",
//                     authorId: effectiveUserId,
//                     replies: 0,
//                     likes: [],
//                     source: "movie_rating_review",
//                     type: "movie_rating_discussion",
//                     tmdbId: Number(id),
//                     movieRatingKey,
//                     movieTitle,
//                     moviePosterPath: detail.poster_path || "",
//                     movieBackdropPath: detail.backdrop_path || "",
//                 });

//                 forumPostId = forumTopicRef.id;
//             }

//             const replyRef = doc(collection(db, "posts", forumPostId, "replies"));
//             const forumPostRef = doc(db, "posts", forumPostId);

//             const reviewBatch = writeBatch(db);

//             reviewBatch.set(replyRef, {
//                 author: username || "Anonyme",
//                 authorId: effectiveUserId,
//                 content: formatMovieReviewContent({
//                     movieTitle,
//                     rating: userMovieRating,
//                     review: trimmedReview,
//                 }),
//                 timestamp: serverTimestamp(),
//                 likes: [],
//                 source: "movie_rating_review",
//                 tmdbId: Number(id),
//                 movieRatingKey,
//                 movieTitle,
//                 movieRating: Number(userMovieRating),
//             });

//             reviewBatch.update(forumPostRef, {
//                 replies: increment(1),
//                 lastActivityAt: serverTimestamp(),
//                 lastActivityBy: effectiveUserId,
//                 lastActivityType: "movie_review",
//             });

//             reviewBatch.update(userRatingRef, {
//                 hasReview: true,
//                 forumPostId,
//                 forumReplyId: replyRef.id,
//                 reviewText: trimmedReview,
//                 reviewUpdatedAt: serverTimestamp(),
//             });

//             reviewBatch.update(userRef, {
//                 "xp.sources.movieReviews": increment(1),
//                 "xp.updatedAt": serverTimestamp(),
//             });

//             reviewBatch.set(profileReviewRef, {
//                 ...buildProfileReviewData({
//                     forumPostId,
//                     forumReplyId: replyRef.id,
//                 }),
//                 createdAt: serverTimestamp(),
//                 visibleOnProfile: true,
//             });

//             await reviewBatch.commit();

//             setMovieReviewMeta({
//                 hasReview: true,
//                 forumPostId,
//                 forumReplyId: replyRef.id,
//                 reviewText: trimmedReview,
//             });

//             setActivityGainToast({
//                 id: `movie-review-${id}-${Date.now()}`,
//                 points: "+4",
//                 unitLabel: t("profilMember.activity"),
//                 label: t("activityToast.movieReviewFirst"),
//                 variant: "review",
//             });

//             console.log("[MovieFrenzy] Avis publié dans le Forum", {
//                 forumPostId,
//                 forumReplyId: replyRef.id,
//                 movieRatingKey,
//                 movieTitle,
//             });

//             setIsMovieReviewModalOpen(false);
//             setMovieReviewDraft("");
//             setMovieReviewForumPostId(forumPostId);

//             setTimeout(() => {
//                 history(`/subject/${forumPostId}`);
//             }, 1200);
//         } catch (error) {
//             console.error("[MovieFrenzy] Erreur lors de la publication/modification de l'avis Forum :", error);
//         }
//     };

//     useEffect(() => {
//         const loadMovieReviewDiscussion = async () => {
//             if (!id) return;

//             try {
//                 const movieRatingKey = String(id);

//                 const reviewTopicQuery = query(
//                     collection(db, "posts"),
//                     where("movieRatingKey", "==", movieRatingKey),
//                     limit(1)
//                 );

//                 const reviewTopicSnap = await getDocs(reviewTopicQuery);

//                 if (reviewTopicSnap.empty) {
//                     setMovieReviewForumPostId(null);
//                     setMovieReviewRepliesCount(0);
//                     return;
//                 }

//                 const reviewTopicDoc = reviewTopicSnap.docs[0];
//                 const reviewTopicData = reviewTopicDoc.data();

//                 setMovieReviewForumPostId(reviewTopicDoc.id);
//                 setMovieReviewRepliesCount(Number(reviewTopicData.replies) || 0);

//             } catch (error) {
//                 console.error("[MovieFrenzy] Erreur lors du chargement du sujet d'avis :", error);
//                 setMovieReviewForumPostId(null);
//                 setMovieReviewRepliesCount(0);
//             }
//         };

//         loadMovieReviewDiscussion();
//     }, [id]);


//     return (
//         <React.Fragment>
//             {/* Helmet pour le SEO */}
//             <Helmet>
//                 <script type="application/ld+json">
//                     {JSON.stringify({
//                         "@context": "https://schema.org",
//                         "@type": "Movie",
//                         "name": detail.title,
//                         "genre": genres.map(genre => genre.name),
//                         "director": directors.map((director) => ({
//                             "@type": "Person",
//                             "name": director.name,
//                             "url": `https://moviefrenzy.ca/acteurDetails/${director.id}?role=director`,
//                         })),
//                         "datePublished": detail.release_date,
//                         "actor": actors.map(actor => ({
//                             "@type": "Person",
//                             "name": actor.name
//                         })),
//                         "aggregateRating": {
//                             "@type": "AggregateRating",
//                             "ratingValue": detail.vote_average,
//                             "reviewCount": detail.vote_count
//                         }
//                     })}
//                 </script>
//             </Helmet>
//             <NavigationBar />
//             <ActivityGainToast
//                 visible={Boolean(activityGainToast)}
//                 toastId={activityGainToast?.id}
//                 points={activityGainToast?.points}
//                 unitLabel={activityGainToast?.unitLabel}
//                 label={activityGainToast?.label}
//                 variant={activityGainToast?.variant}
//                 onDone={() => setActivityGainToast(null)}
//             />
//             {isMovieReviewModalOpen && (
//                 <div className="movie-review-modal-overlay">
//                     <div className="movie-review-modal">
//                         <button
//                             type="button"
//                             className="movie-review-modal__close"
//                             onClick={handleCloseMovieReview}
//                             aria-label={t("common.close")}
//                         >
//                             ×
//                         </button>

//                         <h3 className="movie-review-modal__title">
//                             {movieReviewModalTitle}
//                         </h3>
//                         <p className="movie-review-modal__rating">
//                             {t("movie.rating.yourRating")} : <strong>{userMovieRating}/10</strong>
//                         </p>

//                         <label className="movie-review-modal__label" htmlFor="movie-review-draft">
//                             {t("movie.rating.reviewQuestion")}
//                         </label>

//                         <textarea
//                             id="movie-review-draft"
//                             className="movie-review-modal__textarea"
//                             value={movieReviewDraft}
//                             onChange={(event) => setMovieReviewDraft(event.target.value)}
//                             placeholder={movieReviewPlaceholder}
//                             rows={6}
//                         />

//                         <div className="movie-review-modal__actions">
//                             <button
//                                 type="button"
//                                 className="movie-review-modal__btn movie-review-modal__btn--secondary"
//                                 onClick={handleCloseMovieReview}
//                             >
//                                 {t("movie.rating.cancelReview")}
//                             </button>

//                             <button
//                                 type="button"
//                                 className="movie-review-modal__btn movie-review-modal__btn--primary"
//                                 onClick={handleSubmitMovieReviewDraft}
//                                 disabled={movieReviewDraft.trim().length < 3}
//                             >
//                                 {movieReviewSubmitLabel}
//                             </button>
//                         </div>
//                     </div>
//                 </div>
//             )}

//             <ActeurContext.Provider value={{ handleActeurClick }}>
//                 <div className="ficheFilm" style={{
//                     backgroundImage: `url(${darkwaterImageUrl})`,
//                     backgroundSize: "cover",
//                     backgroundPosition: "center",
//                     backgroundRepeat: "no-repeat",
//                     backgroundAttachment: "fixed",
//                     backgroundBlendMode: "overlay",
//                     backgroundColor: "#200211",
//                     opacity: "0.9",
//                     overflowX: "hidden",
//                     overflowY: "auto",
//                 }}>
//                     <div className="video-wrapper">
//                         <div className="video-background">
//                             {shouldShowVideoCategorySelector && (
//                                 <div
//                                     className="video-category-selector"
//                                     role="group"
//                                     aria-label={t("movie.videoCategories.selectorLabel")}
//                                     style={{
//                                         "--video-category-count":
//                                             availableVideoCategories.length,

//                                         "--video-category-index":
//                                             availableVideoCategories.indexOf(
//                                                 activeVideoCategory
//                                             ),
//                                     }}
//                                 >
//                                     {availableVideoCategories.map((category) => (
//                                         <button
//                                             type="button"
//                                             className="video-category-selector__button"
//                                             key={category}
//                                             disabled={isKinoLoading}
//                                             aria-pressed={
//                                                 activeVideoCategory === category
//                                             }
//                                             onClick={() =>
//                                                 handleVideoCategoryChange(category)
//                                             }
//                                         >
//                                             {videoCategoryLabels[category]}
//                                         </button>
//                                     ))}
//                                 </div>
//                             )}
//                             {videoKey ? (
//                                 <div className="video-overlay">
//                                     <div className="youtube-container">
//                                         <YouTube
//                                             key={id + ":" + currentLanguage + ":" + videoKey}
//                                             videoId={videoKey}
//                                             opts={videoOptions}
//                                             onReady={_onReady}
//                                             onError={_onError}
//                                         />

//                                         <div
//                                             className="logoPinkTownYoutube"
//                                             style={{ backgroundImage: `url(${logoPinkTownBgUrl})` }}
//                                             aria-hidden="true"
//                                         />

//                                         {/* ZONE GRAPHIQUE CAST MOVIEFRENZY */}
//                                         {/* Note : L'icône de Cast native étant restreinte par YouTube dans les iframes web mobiles, */}
//                                         {/* ce bouton reste un élément esthétique haut de gamme pour la charte graphique. */}
//                                         <div className="cast-preview-zone">
//                                             <button
//                                                 type="button"
//                                                 className="moviefrenzy-cast-preview"
//                                                 disabled
//                                                 aria-label={t("movie.castUnavailableAria")}
//                                             >
//                                                 <svg
//                                                     className="moviefrenzy-cast-preview__icon"
//                                                     viewBox="0 0 24 24"
//                                                     aria-hidden="true"
//                                                 >
//                                                     <path d="M1 18v3h3c0-1.66-1.34-3-3-3zm0-4v2c2.76 0 5 2.24 5 5h2c0-3.87-3.13-7-7-7zm0-4v2c4.97 0 9 4.03 9 9h2c0-6.08-4.92-11-11-11zm2-7h18v14h-5v2h5c1.1 0 2-.9 2-2V3c0-1.1-.9-2-2-2H3C1.9 1 1 1.9 1 3v5h2V3z" />
//                                                 </svg>

//                                                 <span>CAST</span>
//                                             </button>
//                                         </div>


//                                     </div>
//                                 </div>
//                             ) : (
//                                 <div className="no-video-background">
//                                     <div className="poster-background" style={{ backgroundImage: `url('https://image.tmdb.org/t/p/w300${detail.poster_path}')`, zIndex: `10000` }}></div>
//                                     <div className="no-video-text-container">
//                                         <h2 className="no-video-text" role="status">
//                                             {t(
//                                                 isKinoLoading
//                                                     ? "movie.kinoLoading"
//                                                     : "movie.noTrailerAvailable"
//                                             )}
//                                         </h2>
//                                     </div>
//                                 </div>
//                             )}
//                         </div>
//                     </div>
//                     <div className="container-fluid">
//                         <div className="row align-items-start">
//                             <div className="col-md-3 d-flex flex-column align-items-start">
//                                 <div id="dateTime">
//                                     <p>
//                                         {t('movie.releaseYear')}: <span id="spanInfoMovie">{detail.release_date?.substring(0, 4)}</span>
//                                         <span> | </span> {t("movie.duration")}:{" "}
//                                         <span id="spanInfoMovie">
//                                             {Number.isFinite(detail.runtime) && detail.runtime > 0
//                                                 ? `${detail.runtime} ${t("movie.minutes")}`
//                                                 : t("movie.durationUnknown")}
//                                         </span>
//                                         {displayedProductionCountries.length > 0 && (
//                                             <>
//                                                 <span className="production-countries-label">
//                                                     <span className="production-countries-separator"> | </span>
//                                                     {t("movie.productionCountries")}:
//                                                 </span>

//                                                 <span className="production-countries">
//                                                     {displayedProductionCountries.map((country) => {
//                                                         const countryName = country.isQuebec
//                                                             ? country.name
//                                                             : (
//                                                                 countryNamesFormatter?.of(country.iso_3166_1) ||
//                                                                 country.name ||
//                                                                 country.iso_3166_1
//                                                             );

//                                                         const tooltipId = `production-country-${country.iso_3166_1}`;

//                                                         return (
//                                                             <span
//                                                                 className="production-country-flag"
//                                                                 key={country.iso_3166_1}
//                                                                 tabIndex={0}
//                                                                 aria-describedby={tooltipId}
//                                                                 onPointerUp={(event) => {
//                                                                     if (event.pointerType === "touch") {
//                                                                         const flagElement = event.currentTarget;

//                                                                         flagElement.focus();

//                                                                         window.setTimeout(() => {
//                                                                             flagElement.blur();
//                                                                         }, 1800);
//                                                                     }
//                                                                 }}
//                                                             >
//                                                                 {country.isQuebec ? (
//                                                                     <img
//                                                                         src={`${process.env.PUBLIC_URL}/images/quebecFlag.svg`}
//                                                                         className="flag-icon production-country-flag__quebec-image"
//                                                                         alt=""
//                                                                         aria-hidden="true"
//                                                                     />
//                                                                 ) : (
//                                                                     <Flag
//                                                                         code={country.iso_3166_1}
//                                                                         className="flag-icon"
//                                                                         alt=""
//                                                                         aria-hidden="true"
//                                                                     />
//                                                                 )}

//                                                                 <span
//                                                                     id={tooltipId}
//                                                                     className="production-country-tooltip"
//                                                                     role="tooltip"
//                                                                 >
//                                                                     {countryName}
//                                                                 </span>
//                                                             </span>
//                                                         );
//                                                     })}
//                                                 </span>

//                                             </>
//                                         )}
//                                     </p>
//                                     {isLoggedIn && (
//                                         <div
//                                             className="favoriteMovie"
//                                             onClick={addFavoritePreview}
//                                             onKeyDown={(event) => {
//                                                 if (
//                                                     event.key === "Enter" ||
//                                                     event.key === " "
//                                                 ) {
//                                                     event.preventDefault();
//                                                     addFavoritePreview();
//                                                 }
//                                             }}
//                                             role="button"
//                                             tabIndex={0}
//                                             aria-label={t("movie.addFavoriteAria")}
//                                         >
//                                             <img
//                                                 src={`${process.env.PUBLIC_URL}/images/favoriteMovie01.png`}
//                                                 alt=""
//                                                 aria-hidden="true"
//                                                 className="coeurMovie"
//                                             />
//                                         </div>
//                                     )}
//                                     {/* 2. Afficher le message selon l'état */}
//                                     {showConfirmation && <h2 style={{ color: "#00FFFF", margin: "8vh 0 0 5vw" }}>{t('movie.addedToFavorites')}</h2>}
//                                     {showLimitMessage && <h2 style={{ color: "#FF0000", margin: "8vh 0 0 5vw" }}>{t('movie.favoritesLimitReached')}</h2>}
//                                     {showAlreadyInFavorites && <h2 style={{ color: "#FFA500", margin: "8vh 0 0 5vw" }}>{t('movie.alreadyInFavorites')}</h2>}
//                                 </div>
//                             </div>
//                             <div className="col-md-6 text-center">
//                                 <h1 className="movie-titleFiche">{detail.title}</h1>
//                                 <div className="movie-genresStyle">
//                                     {genres.map((genre, index) => (
//                                         <Link
//                                             key={genre.id}
//                                             to={{
//                                                 pathname: `/categories/${genre.id}`,
//                                                 state: {
//                                                     id: genre.id,
//                                                     name: genre.name,
//                                                 },
//                                             }}
//                                             className="text-genre"
//                                             style={{ marginRight: "10px" }}
//                                         >
//                                             {genre.name}
//                                         </Link>
//                                     ))}
//                                 </div>
//                             </div>
//                             <div className="col-md-3 d-flex flex-column align-items-start">
//                                 <div className={`sectChart ${isMovieFrenzyRatingMode ? "sectChart--moviefrenzy" : "sectChart--tmdb"}`}>
//                                     <div className="rating-source-toggle" role="group" aria-label={t("movie.rating.sourceToggle")}>
//                                         <button
//                                             type="button"
//                                             className={`rating-source-toggle__btn ${ratingMode === "tmdb" ? "active" : ""
//                                                 }`}
//                                             onClick={() => handleRatingModeChange("tmdb")}
//                                             aria-pressed={ratingMode === "tmdb"}
//                                         >
//                                             <img
//                                                 src={`${process.env.PUBLIC_URL}/images/tmdb.png`}
//                                                 alt=""
//                                                 aria-hidden="true"
//                                                 className="rating-source-toggle__logo"
//                                             />
//                                             <span>TMDB</span>
//                                         </button>

//                                         <button
//                                             type="button"
//                                             className={`rating-source-toggle__btn ${ratingMode === "moviefrenzy" ? "active" : ""
//                                                 }`}
//                                             onClick={() => handleRatingModeChange("moviefrenzy")}
//                                             aria-pressed={ratingMode === "moviefrenzy"}
//                                         >
//                                             <img
//                                                 src={`${process.env.PUBLIC_URL}/images/newColorLogoMovieFrenzy_01.png`}
//                                                 alt=""
//                                                 aria-hidden="true"
//                                                 className="rating-source-toggle__logo"
//                                             />
//                                             <span>MovieFrenzy</span>
//                                         </button>
//                                     </div>

//                                     <h2 className="text-uppercase mb-3 noteCinephile">{ratingTitle}</h2>

//                                     <div className={`chart-container chart-wrapper ${isMovieFrenzyRatingMode ? "chart-wrapper--moviefrenzy" : ""}`}>
//                                         <Doughnut
//                                             key={`${ratingMode}-${isVisible ? "visible-chart" : "hidden-chart"}`}
//                                             data={chartData}
//                                             options={chartOptions}
//                                         />

//                                         <div
//                                             className={`chart-text ${activeRatingPercent === null ? "chart-text--pending" : ""}`}
//                                             style={chartTextStyle}
//                                         >
//                                             {activeRatingPercent === null
//                                                 ? t("movie.rating.noRatingsYet")
//                                                 : `${animatedPercent}%`}
//                                         </div>
//                                     </div>

//                                     <p className="rating-source-caption">{ratingCaption}</p>
//                                     <OmdbInsightsPopover
//                                         data={omdbData}
//                                         movieTitle={detail.title}
//                                     />
//                                     {ratingMode === "moviefrenzy" && movieReviewForumPostId && (
//                                         <button
//                                             type="button"
//                                             className="movie-review-entry__link"
//                                             onClick={() => history(`/subject/${movieReviewForumPostId}`)}
//                                         >
//                                             {movieReviewLinkLabel}
//                                         </button>
//                                     )}

//                                     {ratingMode === "moviefrenzy" && (
//                                         <>
//                                             <div className="user-rating-preview">
//                                                 {isLoggedIn ? (
//                                                     isUserRatingLoading ? (
//                                                         <p>{t("movie.rating.loadingUserRating")}</p>
//                                                     ) : userMovieRating !== null ? (
//                                                         <p>
//                                                             <span className="user-rating-preview__label">{t("movie.rating.yourRating")}</span>
//                                                             <strong>{userMovieRating}/10</strong>
//                                                         </p>
//                                                     ) : (
//                                                         <p>{t("movie.rating.notRatedYet")}</p>
//                                                     )
//                                                 ) : (
//                                                     <p>{t("movie.rating.loginToRate")}</p>
//                                                 )}
//                                             </div>
//                                             {canShareMovieFrenzyReview && (
//                                                 <div className="movie-review-entry">
//                                                     {hasExistingMovieReview ? (
//                                                         <div className="movie-review-entry__actions">
//                                                             <button
//                                                                 type="button"
//                                                                 className="user-rating-save-btn movie-review-entry__button movie-review-entry__button--secondary"
//                                                                 onClick={handleViewMovieReview}
//                                                                 disabled={isDeletingMovieReview}
//                                                             >
//                                                                 {t("movie.rating.viewMyReview")}
//                                                             </button>

//                                                             <button
//                                                                 type="button"
//                                                                 className="user-rating-save-btn movie-review-entry__button"
//                                                                 onClick={handleOpenMovieReview}
//                                                                 disabled={isDeletingMovieReview}
//                                                             >
//                                                                 {t("movie.rating.editReview")}
//                                                             </button>

//                                                             <button
//                                                                 type="button"
//                                                                 className="user-rating-save-btn movie-review-entry__button movie-review-entry__button--danger"
//                                                                 onClick={handleDeleteMovieReview}
//                                                                 disabled={isDeletingMovieReview}
//                                                             >
//                                                                 {isDeletingMovieReview ? t("movie.rating.deletingReview") : t("movie.rating.deleteReview")}
//                                                             </button>
//                                                         </div>
//                                                     ) : (
//                                                         <button
//                                                             type="button"
//                                                             className="user-rating-save-btn movie-review-entry__button"
//                                                             onClick={handleOpenMovieReview}
//                                                         >
//                                                             {t("movie.rating.shareReview")}
//                                                         </button>
//                                                     )}
//                                                     <p className="movie-review-entry__help">
//                                                         {t("movie.rating.reviewHelp")}
//                                                     </p>
//                                                 </div>
//                                             )}

//                                             {isLoggedIn && !isUserRatingLoading && (
//                                                 <div className="user-rating-selector" aria-label={t("movie.rating.ratingSelectorAria")}>
//                                                     <p className="user-rating-selector__title">{t("movie.rating.chooseYourRating")}</p>

//                                                     <div className="user-rating-selector__buttons">
//                                                         {Array.from({ length: 10 }, (_, index) => {
//                                                             const ratingValue = index + 1;

//                                                             return (
//                                                                 <button
//                                                                     key={ratingValue}
//                                                                     type="button"
//                                                                     className={`user-rating-selector__btn ${selectedUserMovieRating === ratingValue ? "active" : ""}`}
//                                                                     onClick={() => {
//                                                                         setSelectedUserMovieRating(ratingValue);
//                                                                         setUserRatingMessage(
//                                                                             t("movie.rating.preparedRating").replace("{{rating}}", ratingValue)
//                                                                         );
//                                                                     }}
//                                                                     aria-pressed={selectedUserMovieRating === ratingValue}
//                                                                 >
//                                                                     {ratingValue}
//                                                                 </button>
//                                                             );
//                                                         })}
//                                                     </div>

//                                                     <button
//                                                         type="button"
//                                                         className="user-rating-save-btn"
//                                                         onClick={handleSaveUserMovieRating}
//                                                         disabled={isSavingUserRating}
//                                                     >
//                                                         {isSavingUserRating ? t("movie.rating.savingRating") : t("movie.rating.saveRating")}
//                                                     </button>
//                                                     {userRatingMessage && (
//                                                         <p className="user-rating-message">{userRatingMessage}</p>
//                                                     )}
//                                                 </div>
//                                             )}
//                                         </>
//                                     )}
//                                 </div>
//                             </div>
//                         </div>
//                         <div ref={overviewSectionRef} className="row movie-overview-container">
//                             <div className="col-12 col-md-4 movie-overview mobile-movie-overview">
//                                 <p>
//                                     {String(detail.overview || "").trim() ||
//                                         t("movie.synopsisUnavailableText")}
//                                 </p>
//                             </div>
//                             <div className="col-md-4 d-flex afficheInfo">
//                                 <div className="col-lg-6">
//                                     <img
//                                         src={`https://image.tmdb.org/t/p/w300${detail.poster_path}`}
//                                         alt={detail.title}
//                                         className="img-fluid imgFluidAffiche"
//                                     />
//                                 </div>
//                                 <div className="col-lg-6">
//                                     <div className="general-info-section" id="infoGeneral">
//                                         <div className="general-info-circle ficheFilmGeneral-Whimsy">
//                                             <span
//                                                 className="ficheFilmGeneral-whimsy-particle ficheFilmGeneral-whimsy-particle--cyan"
//                                                 aria-hidden="true"
//                                             />

//                                             <span
//                                                 className="ficheFilmGeneral-whimsy-particle ficheFilmGeneral-whimsy-particle--magenta"
//                                                 aria-hidden="true"
//                                             />

//                                             <span
//                                                 className="ficheFilmGeneral-whimsy-particle ficheFilmGeneral-whimsy-particle--pink"
//                                                 aria-hidden="true"
//                                             />
//                                             <div className="inner-circle"></div>
//                                         </div>
//                                         <p className="info-text">{t('movie.generalInfo')} <span className="info-highlight">{detail.title}</span></p>
//                                         <p className="info-text">
//                                             {t("movie.director")} :{" "}

//                                             <span className="info-highlight">
//                                                 {directors.length > 0
//                                                     ? directors.map((director, index) => (
//                                                         <React.Fragment key={director.id}>
//                                                             {index > 0 && ", "}

//                                                             <Link
//                                                                 to={`/acteurDetails/${director.id}?role=director`}
//                                                                 className="person-credit-link"
//                                                             >
//                                                                 {director.name}
//                                                             </Link>
//                                                         </React.Fragment>
//                                                     ))
//                                                     : "—"}
//                                             </span>
//                                         </p>
//                                         <p className="info-text">{t('movie.originalTitle')} : <span className="info-highlight">{detail.original_title}</span></p>
//                                         <p className="info-text">{t('movie.screenwriter')} : <span className="info-highlight">{detail.writer}</span></p>
//                                         <div className="info-text maturity-rating">
//                                             <span className="maturity-rating__heading">
//                                                 {t("movie.ageRating")} :
//                                             </span>

//                                             <span
//                                                 className={`
//                                                     mf-maturity-badge
//                                                     mf-maturity-badge--${maturityBadge.level}
//                                                 `}
//                                                 aria-hidden="true"
//                                             >
//                                                 <span className="mf-maturity-badge__system">
//                                                     {isOfficialQuebecRating && (
//                                                         <img
//                                                             src={`${process.env.PUBLIC_URL}/images/quebecFlag.svg`}
//                                                             className="mf-maturity-badge__quebec-flag"
//                                                             alt=""
//                                                         />
//                                                     )}

//                                                     MF · {maturityBadge.system}
//                                                 </span>

//                                                 <span className="mf-maturity-badge__code">
//                                                     {maturityBadge.code}
//                                                 </span>
//                                             </span>

//                                             <span className="info-highlight maturity-rating__label">
//                                                 {maturityLabel}.
//                                             </span>

//                                             {isOfficialQuebecRating && (
//                                                 <div className="quebec-rating-details">
//                                                     <div className="quebec-rating-details__header">
//                                                         <img
//                                                             src={`${process.env.PUBLIC_URL}/images/quebecFlag.svg`}
//                                                             className="quebec-rating-details__flag"
//                                                             alt=""
//                                                             aria-hidden="true"
//                                                         />

//                                                         {maturityRating?.sourceUrl ? (
//                                                             <a
//                                                                 href={maturityRating.sourceUrl}
//                                                                 target="_blank"
//                                                                 rel="noreferrer"
//                                                                 className="quebec-rating-details__source"
//                                                             >
//                                                                 {t("movie.rating.officialQuebecRating")}
//                                                             </a>
//                                                         ) : (
//                                                             <span className="quebec-rating-details__source">
//                                                                 {t("movie.rating.officialQuebecRating")}
//                                                             </span>
//                                                         )}
//                                                     </div>

//                                                     {quebecAdvisoryLabels.length > 0 && (
//                                                         <ul className="quebec-rating-advisories">
//                                                             {quebecAdvisoryLabels.map((advisory) => (
//                                                                 <li
//                                                                     key={advisory}
//                                                                     className="quebec-rating-advisories__item"
//                                                                 >
//                                                                     {advisory}
//                                                                 </li>
//                                                             ))}
//                                                         </ul>
//                                                     )}
//                                                 </div>
//                                             )}
//                                         </div>
//                                     </div>
//                                 </div>
//                             </div>
//                             <div className="col-md-12">
//                                 <Container className="mt-5 actors-container">
//                                     <div className="movie-actors">
//                                         <h5>{t('movie.actors')}:</h5>
//                                         <div className="actors-grid">
//                                             {actors && actors.slice(0, 4).map((actor, index) => (
//                                                 <div
//                                                     key={actor.id || index}
//                                                     className="actor-card"
//                                                 >
//                                                     <Link
//                                                         to={{
//                                                             pathname: `/acteurDetails/${actor.id}`,
//                                                             state: { id: actor.id },
//                                                         }}
//                                                         className="linkActorFicheFilm"
//                                                     >
//                                                         <img
//                                                             src={
//                                                                 actor.profile_path
//                                                                     ? `https://image.tmdb.org/t/p/w500${actor.profile_path}`
//                                                                     : "https://zupimages.net/up/23/14/zdoe.jpg"
//                                                             }
//                                                             alt=""
//                                                             aria-hidden="true"
//                                                             className={`substitute-image${index === 3 ? " mainActors-image4" : ""
//                                                                 }`}
//                                                         />

//                                                         <div className="actor-name-container">
//                                                             <li
//                                                                 className="actor-name"
//                                                             >
//                                                                 {actor.original_name}
//                                                             </li>

//                                                             <li className="actor-name actor-character">
//                                                                 {actor.character}
//                                                             </li>
//                                                         </div>
//                                                     </Link>
//                                                 </div>
//                                             ))}
//                                         </div>
//                                         {actors && actors.length > 4 && (
//                                             <div className="row">
//                                                 <div className="col-lg-6">
//                                                     <div className="secondary-roles">
//                                                         <h5>{t('movie.secondaryRoles')}:</h5>
//                                                         <p>
//                                                             {actors.slice(4).map((actor, index) => (
//                                                                 <span key={index}>{actor.original_name}{index < actors.slice(4).length - 1 && ", "}</span>
//                                                             ))}
//                                                         </p>
//                                                     </div>
//                                                 </div>
//                                             </div>
//                                         )}
//                                     </div>
//                                 </Container>
//                             </div>
//                         </div>
//                     </div>
//                 </div>
//             </ActeurContext.Provider>
//             <div className="actor-details-footer"
//                 style={{ maxWidth: '100%', maxHeight: '100%', position: 'relative' }}
//             >
//                 <Footer />
//             </div>
//         </React.Fragment>

//     );
// };

// export default FicheFilm;





